import { MailerSettingsSection } from "./MailerSettingsSection.tsx";
import type {
  AdminAnalytics,
  AdminMeetingView,
  AdminUser,
  DailyReport,
  DevComment,
  DevDeployment,
  DevEngine,
  DevRequest,
  DevRequestLog,
  DevRequestTargetEnv,
  DevRequestType,
  DocumentRecord,
  HintStrategy,
  Invite,
  MarketplaceTool,
  ProductFeedbackCategory,
  ProductFeedbackReport,
  ReleaseNote,
  SystemReview,
  UserStatus,
  UserTier,
  VoiceOfFieldReport,
} from "@scoach/types";
import { ALL_ROLES } from "@scoach/types";
import { useToast } from "@scoach/ui";
import { Chev, User as UserIcon } from "@scoach/ui/icons";
import { Link, useNavigate } from "@tanstack/react-router";
import { collection, doc, onSnapshot, orderBy, query, where } from "firebase/firestore";
import { useEffect, useRef, useState } from "react";

import { ErrorBoundary } from "../../../components/ErrorBoundary.tsx";
import { isAdminEmail, isSuperAdminEmail } from "../../../lib/admins.ts";
import { connectGoogleDrive } from "../../../lib/firebase.ts";
import { getDb } from "../../../lib/firestore.ts";
import { safeHref } from "../../../lib/safeHref.ts";
import { useAuthStore } from "../../auth/store.ts";
import { DashHeader } from "../../dashboard/components/DashHeader.tsx";
import { ReviewThread } from "../../reviews/ReviewThread.tsx";
import { reviewsApi } from "../../reviews/api.ts";
import { adminApi } from "../api.ts";
import { MeetingsDailyBarChart, MeetingsLineChart, RolesBarChart } from "./AdminCharts.tsx";
import { ClientErrorsSection } from "./ClientErrorsSection.tsx";

function formatDate(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function formatDateTime(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function Stars({ n }: { n: number }) {
  return (
    <span style={{ color: "#F5B400", letterSpacing: 1 }} aria-label={`${n} of 5 stars`}>
      {"★".repeat(Math.max(0, Math.min(5, n)))}
      <span style={{ color: "var(--text-4)" }}>{"★".repeat(5 - Math.max(0, Math.min(5, n)))}</span>
    </span>
  );
}

function ReviewsSection() {
  const [reviews, setReviews] = useState<SystemReview[] | null>(null);

  useEffect(() => {
    reviewsApi
      .listAll()
      .then((r) => setReviews(r.reviews))
      .catch(() => setReviews([]));
  }, []);

  function updateReview(updated: SystemReview) {
    setReviews((prev) =>
      prev ? prev.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)) : prev,
    );
  }

  if (!reviews) return null;

  const avg =
    reviews.length > 0
      ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)
      : "—";

  return (
    <section style={{ marginTop: 28 }}>
      <h2
        style={{
          fontFamily: "var(--font-display)",
          fontSize: 18,
          fontWeight: 700,
          marginBottom: 4,
        }}
      >
        System feedback
      </h2>
      <p style={{ fontSize: 13, color: "var(--text-3)", marginBottom: 14 }}>
        {reviews.length} review{reviews.length !== 1 ? "s" : ""}
        {reviews.length > 0 && ` · average ${avg} ★`}
      </p>
      {reviews.length === 0 ? (
        <div
          style={{
            padding: 24,
            textAlign: "center",
            color: "var(--text-3)",
            background: "var(--surface)",
            border: "1px solid var(--border-soft)",
            borderRadius: 12,
          }}
        >
          No feedback yet.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {reviews.map((r) => (
            <div
              key={r.id}
              style={{
                background: "var(--surface)",
                border: "1px solid var(--border-soft)",
                borderRadius: 10,
                padding: "12px 16px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  marginBottom: r.text ? 6 : 0,
                }}
              >
                <Stars n={r.rating} />
                <span style={{ fontWeight: 600, fontSize: 13, color: "var(--text-1)" }}>
                  {r.name || r.email.split("@")[0]}
                </span>
                <span
                  style={{ fontSize: 12, color: "var(--text-3)", fontFamily: "var(--font-mono)" }}
                >
                  {r.email}
                </span>
                <span style={{ marginLeft: "auto", fontSize: 12, color: "var(--text-3)" }}>
                  {formatDateTime(r.createdAt)}
                </span>
              </div>
              {r.text && (
                <div dir="auto" style={{ fontSize: 13, color: "var(--text-2)", lineHeight: 1.5 }}>
                  {r.text}
                </div>
              )}
              {r.lastMeetingId && (
                <div style={{ marginTop: 8 }}>
                  <Link
                    to="/meetings/$id/summary"
                    params={{ id: r.lastMeetingId }}
                    target="_blank"
                    style={{ fontSize: 12, color: "var(--gc-blue)", textDecoration: "none" }}
                  >
                    ↗ Last meeting{r.lastMeetingTitle ? `: ${r.lastMeetingTitle}` : ""}
                  </Link>
                </div>
              )}
              <div
                style={{
                  marginTop: 10,
                  paddingTop: 10,
                  borderTop: "1px solid var(--border-soft)",
                }}
              >
                <ReviewThread review={r} viewerSide="admin" onChange={updateReview} />
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function DailyReportSection() {
  const [report, setReport] = useState<DailyReport | null>(null);
  const [dates, setDates] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [selected, setSelected] = useState<string>("");
  // Day to (re)generate — defaults to today (UTC), matching the report's day boundaries.
  const [genDate, setGenDate] = useState<string>(new Date().toISOString().slice(0, 10));

  useEffect(() => {
    Promise.all([adminApi.fetchDailyReport(), adminApi.fetchDailyReports()])
      .then(([latest, all]) => {
        setReport(latest);
        setDates(all.map((r) => r.date));
        setSelected(latest?.date ?? "");
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  function load(date: string) {
    setSelected(date);
    setLoading(true);
    adminApi
      .fetchDailyReport(date || undefined)
      .then(setReport)
      .catch(() => {})
      .finally(() => setLoading(false));
  }

  function runNow() {
    setRunning(true);
    adminApi
      .runDailyReport(genDate || undefined)
      .then((r) => {
        setReport(r);
        setDates((prev) => (prev.includes(r.date) ? prev : [r.date, ...prev].sort().reverse()));
        setSelected(r.date);
      })
      .catch(() => {})
      .finally(() => setRunning(false));
  }

  return (
    <section style={{ marginTop: 28 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
        <h2 style={{ fontFamily: "var(--font-display)", fontSize: 18, fontWeight: 700, flex: 1 }}>
          Daily meeting report
        </h2>
        {dates.length > 0 && (
          <select
            value={selected}
            onChange={(e) => load(e.target.value)}
            style={{ padding: "6px 8px", borderRadius: 8 }}
          >
            {dates.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        )}
        <input
          type="date"
          value={genDate}
          max={new Date().toISOString().slice(0, 10)}
          onChange={(e) => setGenDate(e.target.value)}
          title="Day to generate"
          style={{ padding: "6px 8px", borderRadius: 8 }}
        />
        <button type="button" className="pill-btn" disabled={running} onClick={runNow}>
          {running ? "Running…" : "Run for this day"}
        </button>
      </div>
      <p style={{ fontSize: 13, color: "var(--text-3)", marginBottom: 14 }}>
        A daily AI analysis of all users' meetings — who used Sally, recurring problems, product
        gaps, and any system errors. Generated automatically each morning; pick a day and “Run for
        this day” to (re)generate on demand (defaults to today).
      </p>

      {loading ? (
        <div style={{ padding: 24, color: "var(--text-3)" }}>Loading…</div>
      ) : !report ? (
        <div
          style={{
            padding: 24,
            textAlign: "center",
            color: "var(--text-3)",
            background: "var(--surface)",
            border: "1px solid var(--border-soft)",
            borderRadius: 12,
          }}
        >
          No report yet. Click “Run now” to generate one.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div
            style={{
              display: "flex",
              gap: 20,
              flexWrap: "wrap",
              fontSize: 13,
              color: "var(--text-2)",
            }}
          >
            <span>
              <strong>{report.meetingsAnalyzed}</strong> meetings
            </span>
            <span>
              <strong>{report.usersActive}</strong> active users
            </span>
            <span>
              <strong>{report.sttIssues ?? 0}</strong> transcription issues
            </span>
            <span style={{ color: "var(--text-4)" }}>
              Generated {formatDateTime(report.generatedAt)}
            </span>
          </div>

          <div
            dir="auto"
            style={{
              background: "var(--surface)",
              border: "1px solid var(--border-soft)",
              borderRadius: 10,
              padding: "12px 16px",
              fontSize: 14,
              lineHeight: 1.5,
            }}
          >
            {report.summary}
          </div>

          {report.systemErrors && report.systemErrors.length > 0 && (
            <div
              style={{
                background: "var(--gc-red-50, #fdecea)",
                border: "1px solid var(--gc-red, #d93025)",
                borderRadius: 10,
                padding: "10px 14px",
              }}
            >
              <div
                style={{
                  fontWeight: 700,
                  fontSize: 14,
                  marginBottom: 6,
                  color: "var(--gc-red, #b00020)",
                }}
              >
                ⚠️ System errors detected ({report.systemErrors.length})
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {report.systemErrors.map((e, i) => (
                  <div key={i} style={{ fontSize: 13, color: "var(--text-2)" }}>
                    <span
                      style={{
                        fontFamily: "var(--font-mono)",
                        fontSize: 11,
                        padding: "1px 6px",
                        borderRadius: 4,
                        background: "var(--surface)",
                        marginRight: 6,
                      }}
                    >
                      {e.kind}
                    </span>
                    <strong>{e.user}</strong> — {e.detail}{" "}
                    <Link
                      to="/meetings/$id/summary"
                      params={{ id: e.meetingId }}
                      target="_blank"
                      style={{ color: "var(--gc-blue)", textDecoration: "none" }}
                    >
                      (open)
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          )}

          {report.users && report.users.length > 0 && (
            <div
              style={{
                background: "var(--surface)",
                border: "1px solid var(--border-soft)",
                borderRadius: 10,
                padding: "10px 14px",
              }}
            >
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>
                Users active this day ({report.users.length})
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ textAlign: "left", color: "var(--text-3)" }}>
                      <th style={{ padding: "4px 8px" }}>User</th>
                      <th style={{ padding: "4px 8px" }}>Meetings</th>
                      <th style={{ padding: "4px 8px" }}>Minutes</th>
                      <th style={{ padding: "4px 8px" }}>Types</th>
                      <th style={{ padding: "4px 8px" }}>STT errors</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.users.map((u) => (
                      <tr key={u.uid} style={{ borderTop: "1px solid var(--border-soft)" }}>
                        <td style={{ padding: "4px 8px" }}>
                          <div style={{ fontWeight: 600 }}>{u.name}</div>
                          <div
                            style={{
                              fontSize: 11,
                              color: "var(--text-4)",
                              fontFamily: "var(--font-mono)",
                            }}
                          >
                            {u.email}
                          </div>
                        </td>
                        <td style={{ padding: "4px 8px" }}>{u.meetings}</td>
                        <td style={{ padding: "4px 8px" }}>{u.minutes}</td>
                        <td style={{ padding: "4px 8px", color: "var(--text-3)" }}>
                          {u.meetingTypes.join(", ")}
                        </td>
                        <td
                          style={{
                            padding: "4px 8px",
                            color: u.sttIssues > 0 ? "var(--gc-red, #d93025)" : "var(--text-4)",
                          }}
                        >
                          {u.sttIssues}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {report.themes.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {report.themes.map((t, i) => (
                <div
                  key={i}
                  style={{
                    background: "var(--surface)",
                    border: "1px solid var(--border-soft)",
                    borderRadius: 10,
                    padding: "10px 14px",
                  }}
                >
                  <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>
                    {t.title}
                    {typeof t.count === "number" && (
                      <span style={{ marginLeft: 8, fontSize: 12, color: "var(--text-4)" }}>
                        ×{t.count}
                      </span>
                    )}
                  </div>
                  <div dir="auto" style={{ fontSize: 13, color: "var(--text-2)", lineHeight: 1.5 }}>
                    {t.detail}
                  </div>
                </div>
              ))}
            </div>
          )}

          {report.productGaps && report.productGaps.length > 0 && (
            <div
              style={{
                background: "var(--surface)",
                border: "1px solid var(--border-soft)",
                borderRadius: 10,
                padding: "10px 14px",
              }}
            >
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 6 }}>
                Product gaps & asks
              </div>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: "var(--text-2)" }}>
                {report.productGaps.map((g, i) => (
                  <li key={i} dir="auto" style={{ marginBottom: 2 }}>
                    {g}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

const DEV_STATUS_FLOW: DevRequest["status"][] = [
  "pending",
  "planning",
  "plan-ready",
  "building",
  "qc-deployed",
  "promoting",
  "done",
];

function devStatusColor(s: DevRequest["status"]): string {
  if (s === "done") return "var(--gc-green, #137333)";
  if (s === "failed" || s === "rejected") return "var(--gc-red, #d93025)";
  if (s === "plan-ready" || s === "qc-deployed") return "var(--gc-yellow, #f9ab00)";
  return "var(--gc-blue, #1a73e8)"; // planning / revising-* / building / promoting
}

/**
 * The "Voice of the field" admin tab hosts two AI reports over all real client
 * meetings, switched via a sub-tab:
 *  - "Field asks": deduped bugs + feature requests grouped by Google product.
 *  - "Product feedback": a deeper per-product analysis for Google PMs & Eng —
 *    works-well, categorized blockers, and the customers who hit each (with
 *    expandable use case / request / deal value / verbatim quotes).
 */
function VoiceOfFieldSection() {
  const [subtab, setSubtab] = useState<"asks" | "feedback">("asks");
  return (
    <section style={{ marginTop: 28 }}>
      <div className="seg seg-tabs" style={{ marginBottom: 20, flexWrap: "wrap" }}>
        <button
          type="button"
          className={subtab === "asks" ? "on" : ""}
          onClick={() => setSubtab("asks")}
        >
          Field asks
        </button>
        <button
          type="button"
          className={subtab === "feedback" ? "on" : ""}
          onClick={() => setSubtab("feedback")}
        >
          Product feedback
        </button>
      </div>
      {subtab === "asks" ? <FieldAsksSubReport /> : <ProductFeedbackSubReport />}
    </section>
  );
}

/**
 * "Field asks": a daily AI pass over all real client meetings that surfaces the
 * bugs + feature requests clients are asking for, grouped by Google product,
 * naming the client(s) behind each ask. Exportable to Excel / PDF.
 */
function FieldAsksSubReport() {
  const toast = useToast();
  const [report, setReport] = useState<VoiceOfFieldReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    adminApi
      .fetchVoiceOfField()
      .then(setReport)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  function runNow() {
    setRunning(true);
    adminApi
      .runVoiceOfField()
      .then(setReport)
      .catch((e) => toast.push({ tone: "error", message: (e as Error).message }))
      .finally(() => setRunning(false));
  }

  async function download(kind: "xlsx" | "pdf") {
    try {
      const blob =
        kind === "xlsx"
          ? await adminApi.exportVoiceOfFieldXlsx()
          : await adminApi.exportVoiceOfFieldPdf();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `voice-of-the-field.${kind}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.push({ tone: "error", message: (e as Error).message });
    }
  }

  const hasData = !!report && report.products.length > 0;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
        <h2 style={{ fontFamily: "var(--font-display)", fontSize: 18, fontWeight: 700, flex: 1 }}>
          Field asks
        </h2>
        <button
          type="button"
          className="ghost-btn"
          disabled={!hasData}
          onClick={() => download("xlsx")}
        >
          Export Excel
        </button>
        <button
          type="button"
          className="ghost-btn"
          disabled={!hasData}
          onClick={() => download("pdf")}
        >
          Export PDF
        </button>
        <button type="button" className="pill-btn" disabled={running} onClick={runNow}>
          {running ? "Analyzing…" : "Run now"}
        </button>
      </div>
      <p style={{ fontSize: 13, color: "var(--text-3)", marginBottom: 14 }}>
        For Google product managers: the bugs and feature requests clients are asking for across all
        real meetings, grouped by product. Each ask lists the client(s) who raised it (with a count
        when more than one). Refreshed automatically each day; “Run now” ingests any new meetings
        and re-aggregates.
      </p>

      {loading ? (
        <div style={{ padding: 24, color: "var(--text-3)" }}>Loading…</div>
      ) : !report ? (
        <div
          style={{
            padding: 24,
            textAlign: "center",
            color: "var(--text-3)",
            background: "var(--surface)",
            border: "1px solid var(--border-soft)",
            borderRadius: 12,
          }}
        >
          No analysis yet. Click “Run now” to generate the first report.
        </div>
      ) : (
        <>
          <div
            style={{
              display: "flex",
              gap: 20,
              flexWrap: "wrap",
              fontSize: 13,
              color: "var(--text-2)",
              marginBottom: 16,
            }}
          >
            <span>
              <strong>{report.totalMeetingsSeen}</strong> meetings analyzed
            </span>
            <span>
              <strong>{report.products.length}</strong> products
            </span>
            <span style={{ color: "var(--text-4)" }}>
              Generated {formatDateTime(report.generatedAt)}
            </span>
          </div>

          {!hasData ? (
            <div style={{ padding: 24, color: "var(--text-3)" }}>
              No client bug or feature signals found yet.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              {report.products.map((group) => (
                <div
                  key={group.product}
                  style={{
                    background: "var(--surface)",
                    border: "1px solid var(--border-soft)",
                    borderRadius: 12,
                    padding: 16,
                  }}
                >
                  <h3
                    style={{
                      fontSize: 15,
                      fontWeight: 700,
                      color: "var(--gc-blue)",
                      marginBottom: 10,
                    }}
                  >
                    {group.product}
                  </h3>
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {group.items.map((item, i) => (
                      <div
                        key={i}
                        style={{
                          borderTop: i ? "1px solid var(--border-soft)" : "none",
                          paddingTop: i ? 10 : 0,
                        }}
                      >
                        <div
                          style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}
                        >
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 700,
                              letterSpacing: 0.4,
                              padding: "2px 8px",
                              borderRadius: 999,
                              color:
                                item.kind === "bug"
                                  ? "var(--gc-red, #d93025)"
                                  : "var(--gc-green, #137333)",
                              background:
                                item.kind === "bug"
                                  ? "var(--gc-red-50, #fce8e6)"
                                  : "var(--gc-green-50, #e6f4ea)",
                            }}
                          >
                            {item.kind === "bug" ? "BUG" : "FEATURE"}
                          </span>
                          <span style={{ fontWeight: 600, fontSize: 14, color: "var(--text-1)" }}>
                            {item.title}
                          </span>
                          <span
                            style={{ marginLeft: "auto", fontSize: 12, color: "var(--text-3)" }}
                          >
                            {item.clientCount} {item.clientCount === 1 ? "client" : "clients"}
                          </span>
                        </div>
                        {item.detail && (
                          <div style={{ fontSize: 13, color: "var(--text-2)", marginBottom: 6 }}>
                            {item.detail}
                          </div>
                        )}
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                          {item.clients.map((c, j) => (
                            <Link
                              key={j}
                              to="/meetings/$id/summary"
                              params={{ id: c.meetingId }}
                              className="pill-tag"
                              title={c.meetingTitle ?? c.name}
                              style={{
                                fontSize: 11,
                                padding: "2px 8px",
                                borderRadius: 999,
                                background: "var(--bg-2)",
                                color: "var(--text-2)",
                                textDecoration: "none",
                              }}
                            >
                              {c.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * "Product feedback": a deeper AI analysis of Google-product feedback across all
 * real client meetings, for Google PMs & Engineering. Groups findings by product,
 * then by blocker/issue category, clustering the customers who hit the same
 * blocker. Each customer expands to reveal their use case, specific request, deal
 * value, and verbatim quotes. Exportable to Excel.
 */
function ProductFeedbackSubReport() {
  const toast = useToast();
  const [report, setReport] = useState<ProductFeedbackReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    adminApi
      .fetchProductFeedback()
      .then(setReport)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  function runNow() {
    setRunning(true);
    adminApi
      .runProductFeedback()
      .then(setReport)
      .catch((e) => toast.push({ tone: "error", message: (e as Error).message }))
      .finally(() => setRunning(false));
  }

  async function downloadXlsx() {
    try {
      const blob = await adminApi.exportProductFeedbackXlsx();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "product-feedback.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.push({ tone: "error", message: (e as Error).message });
    }
  }

  const hasData = !!report && report.products.length > 0;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
        <h2 style={{ fontFamily: "var(--font-display)", fontSize: 18, fontWeight: 700, flex: 1 }}>
          Product feedback
        </h2>
        <button type="button" className="ghost-btn" disabled={!hasData} onClick={downloadXlsx}>
          Export Excel
        </button>
        <button type="button" className="pill-btn" disabled={running} onClick={runNow}>
          {running ? "Analyzing…" : "Run now"}
        </button>
      </div>
      <p style={{ fontSize: 13, color: "var(--text-3)", marginBottom: 14 }}>
        For Google product managers &amp; engineering: a comprehensive analysis of Google-product
        feedback across all real meetings. Grouped by product, then by the type of blocker, with the
        customers who hit each one clustered together. Expand a customer to see their use case,
        specific request, deal value, and verbatim quotes. Refreshed automatically each day; “Run
        now” ingests any new meetings and re-aggregates.
      </p>

      {loading ? (
        <div style={{ padding: 24, color: "var(--text-3)" }}>Loading…</div>
      ) : !report ? (
        <div
          style={{
            padding: 24,
            textAlign: "center",
            color: "var(--text-3)",
            background: "var(--surface)",
            border: "1px solid var(--border-soft)",
            borderRadius: 12,
          }}
        >
          No analysis yet. Click “Run now” to generate the first report.
        </div>
      ) : (
        <>
          <div
            style={{
              display: "flex",
              gap: 20,
              flexWrap: "wrap",
              fontSize: 13,
              color: "var(--text-2)",
              marginBottom: 16,
            }}
          >
            <span>
              <strong>{report.totalMeetingsSeen}</strong> meetings analyzed
            </span>
            <span>
              <strong>{report.products.length}</strong> products
            </span>
            <span style={{ color: "var(--text-4)" }}>
              Generated {formatDateTime(report.generatedAt)}
            </span>
          </div>

          {!hasData ? (
            <div style={{ padding: 24, color: "var(--text-3)" }}>
              No Google-product feedback found yet.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              {report.products.map((group) => (
                <div
                  key={group.product}
                  style={{
                    background: "var(--surface)",
                    border: "1px solid var(--border-soft)",
                    borderRadius: 12,
                    padding: 16,
                  }}
                >
                  <h3
                    style={{
                      fontSize: 15,
                      fontWeight: 700,
                      color: "var(--gc-blue)",
                      marginBottom: 10,
                    }}
                  >
                    {group.product}
                  </h3>

                  {group.worksWell.length > 0 && (
                    <div style={{ marginBottom: 12 }}>
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          letterSpacing: 0.4,
                          color: "var(--gc-green, #137333)",
                          marginBottom: 6,
                        }}
                      >
                        WHAT WORKS WELL
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {group.worksWell.map((w, i) => (
                          <span
                            key={i}
                            style={{
                              fontSize: 12,
                              padding: "3px 10px",
                              borderRadius: 999,
                              background: "var(--gc-green-50, #e6f4ea)",
                              color: "var(--gc-green, #137333)",
                            }}
                          >
                            {w}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                    {group.blockers.map((blocker, i) => (
                      <div
                        key={i}
                        style={{
                          borderTop: i ? "1px solid var(--border-soft)" : "none",
                          paddingTop: i ? 12 : 0,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            marginBottom: 4,
                            flexWrap: "wrap",
                          }}
                        >
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 700,
                              letterSpacing: 0.4,
                              padding: "2px 8px",
                              borderRadius: 999,
                              ...categoryBadgeStyle(blocker.category),
                            }}
                          >
                            {categoryLabel(blocker.category)}
                          </span>
                          <span style={{ fontWeight: 600, fontSize: 14, color: "var(--text-1)" }}>
                            {blocker.title}
                          </span>
                          <span
                            style={{ marginLeft: "auto", fontSize: 12, color: "var(--text-3)" }}
                          >
                            {blocker.customerCount}{" "}
                            {blocker.customerCount === 1 ? "customer" : "customers"}
                          </span>
                        </div>
                        {blocker.detail && (
                          <div style={{ fontSize: 13, color: "var(--text-2)", marginBottom: 8 }}>
                            {blocker.detail}
                          </div>
                        )}
                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                          {blocker.customers.map((c, j) => (
                            <details
                              key={j}
                              style={{
                                background: "var(--bg-2)",
                                border: "1px solid var(--border-soft)",
                                borderRadius: 8,
                                padding: "6px 10px",
                                fontSize: 13,
                              }}
                            >
                              <summary
                                style={{
                                  cursor: "pointer",
                                  fontWeight: 600,
                                  color: "var(--text-1)",
                                  listStyle: "revert",
                                }}
                              >
                                {c.name}
                                {c.date ? (
                                  <span style={{ fontWeight: 400, color: "var(--text-4)" }}>
                                    {" "}
                                    · {formatDateTime(c.date)}
                                  </span>
                                ) : null}
                              </summary>
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 8,
                                  marginTop: 8,
                                }}
                              >
                                <DetailRow label="Use case" value={c.useCase} />
                                <DetailRow label="Request" value={c.request} />
                                <DetailRow
                                  label="Opportunity / Deal value"
                                  value={c.dealValue || "Not mentioned"}
                                />
                                {c.quotes.length > 0 && (
                                  <div>
                                    <div
                                      style={{
                                        fontSize: 11,
                                        fontWeight: 700,
                                        letterSpacing: 0.3,
                                        color: "var(--text-3)",
                                        marginBottom: 4,
                                      }}
                                    >
                                      DIRECT QUOTES
                                    </div>
                                    {c.quotes.map((q, k) => (
                                      <blockquote
                                        key={k}
                                        style={{
                                          margin: "0 0 6px",
                                          padding: "4px 10px",
                                          borderLeft: "3px solid var(--gc-blue)",
                                          color: "var(--text-2)",
                                          fontStyle: "italic",
                                        }}
                                      >
                                        {q.speaker ? <strong>{q.speaker}: </strong> : null}
                                        {q.text}
                                      </blockquote>
                                    ))}
                                  </div>
                                )}
                                <Link
                                  to="/meetings/$id/summary"
                                  params={{ id: c.meetingId }}
                                  style={{
                                    fontSize: 12,
                                    color: "var(--gc-blue)",
                                    textDecoration: "none",
                                  }}
                                >
                                  Open meeting: {c.meetingTitle ?? c.meetingId} ↗
                                </Link>
                              </div>
                            </details>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/** One labelled field inside an expanded customer's details. */
function DetailRow({ label, value }: { label: string; value?: string }) {
  return (
    <div>
      <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.3, color: "var(--text-3)" }}>
        {label.toUpperCase()}
      </span>
      <div style={{ fontSize: 13, color: "var(--text-2)" }}>{value?.trim() || "Not mentioned"}</div>
    </div>
  );
}

/** Short human label for a product-feedback category. */
function categoryLabel(c: ProductFeedbackCategory): string {
  switch (c) {
    case "bug":
      return "BUG";
    case "missing-feature":
      return "MISSING FEATURE";
    case "feature-gap":
      return "FEATURE GAP";
    case "blocker":
      return "BLOCKER";
    case "performance":
      return "PERFORMANCE";
    default:
      return "OTHER";
  }
}

/** Badge colors per category (red-ish for breaking, amber for gaps, blue for other). */
function categoryBadgeStyle(c: ProductFeedbackCategory): {
  color: string;
  background: string;
} {
  switch (c) {
    case "bug":
    case "blocker":
      return { color: "var(--gc-red, #d93025)", background: "var(--gc-red-50, #fce8e6)" };
    case "missing-feature":
    case "feature-gap":
      return { color: "var(--gc-yellow, #b06000)", background: "var(--gc-yellow-50, #fef7e0)" };
    case "performance":
      return { color: "var(--gc-blue, #1a73e8)", background: "var(--gc-blue-50, #e8f0fe)" };
    default:
      return { color: "var(--text-2)", background: "var(--bg-2)" };
  }
}

/**
 * Self-serve dev agent (moshem-only): file a bug/feature, watch it flow
 * pending → planning → plan-ready → building → qc-deployed → promoting → done,
 * approving at the plan and prod gates. Live via Firestore onSnapshot.
 */
function DevRequestsSection() {
  const toast = useToast();
  const [requests, setRequests] = useState<DevRequest[]>([]);
  const [type, setType] = useState<DevRequestType>("bug");
  const [targetEnv, setTargetEnv] = useState<DevRequestTargetEnv>("qc");
  const [engine, setEngine] = useState<DevEngine>("gemini-3.8-flash");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [logs, setLogs] = useState<DevRequestLog[]>([]);
  const [comments, setComments] = useState<DevComment[]>([]);
  const [feedbackDraft, setFeedbackDraft] = useState<Record<string, string>>({});
  const [historyEnv, setHistoryEnv] = useState<DevRequestTargetEnv>("qc");
  const [deployments, setDeployments] = useState<DevDeployment[]>([]);

  // A feature can only target QC; prod is a bug-fix-only path.
  useEffect(() => {
    if (type === "feature" && targetEnv === "prod") setTargetEnv("qc");
  }, [type, targetEnv]);

  // Live queue.
  useEffect(() => {
    try {
      const unsub = onSnapshot(
        query(collection(getDb(), "devRequests"), orderBy("createdAt", "desc")),
        (snap) => setRequests(snap.docs.map((d) => d.data() as DevRequest)),
        (err) => console.warn("[dev-requests] listener error", err),
      );
      return () => unsub();
    } catch (err) {
      console.warn("[dev-requests] failed to subscribe", err);
    }
  }, []);

  // Live version-history / rollback ledger for the selected environment.
  useEffect(() => {
    try {
      const unsub = onSnapshot(
        query(
          collection(getDb(), "devDeployments"),
          where("env", "==", historyEnv),
          orderBy("createdAt", "desc"),
        ),
        (snap) => setDeployments(snap.docs.map((d) => d.data() as DevDeployment)),
        (err) => console.warn("[dev-deployments] listener error", err),
      );
      return () => unsub();
    } catch {
      /* noop */
    }
  }, [historyEnv]);

  // Live logs + comments for the selected request.
  useEffect(() => {
    if (!selected) {
      setLogs([]);
      setComments([]);
      return;
    }
    const unsubs: Array<() => void> = [];
    try {
      unsubs.push(
        onSnapshot(
          query(collection(doc(getDb(), "devRequests", selected), "logs"), orderBy("at", "asc")),
          (snap) => setLogs(snap.docs.map((d) => d.data() as DevRequestLog)),
          (err) => console.warn("[dev-requests] logs listener error", err),
        ),
      );
      unsubs.push(
        onSnapshot(
          query(
            collection(doc(getDb(), "devRequests", selected), "comments"),
            orderBy("createdAt", "asc"),
          ),
          (snap) => setComments(snap.docs.map((d) => d.data() as DevComment)),
          (err) => console.warn("[dev-requests] comments listener error", err),
        ),
      );
    } catch {
      /* noop */
    }
    return () => unsubs.forEach((u) => u());
  }, [selected]);

  async function submit() {
    if (!description.trim() || submitting) return;
    setSubmitting(true);
    try {
      await adminApi.createDevRequest(type, title.trim(), description.trim(), targetEnv, engine);
      setTitle("");
      setDescription("");
      toast.push({ tone: "success", message: "Request filed — the agent is planning it." });
    } catch (e) {
      toast.push({ tone: "error", message: (e as Error).message });
    } finally {
      setSubmitting(false);
    }
  }

  async function act(fn: () => Promise<unknown>, id: string) {
    setBusyId(id);
    try {
      await fn();
    } catch (e) {
      toast.push({ tone: "error", message: (e as Error).message });
    } finally {
      setBusyId(null);
    }
  }

  const inputStyle = {
    padding: "8px 12px",
    borderRadius: 8,
    border: "1px solid var(--border)",
    fontSize: 13,
    background: "var(--surface)",
    color: "var(--text-1)",
  } as const;

  return (
    <section style={{ marginTop: 28 }}>
      <h2
        style={{
          fontFamily: "var(--font-display)",
          fontSize: 18,
          fontWeight: 700,
          marginBottom: 4,
        }}
      >
        Dev agent
      </h2>
      <p style={{ fontSize: 13, color: "var(--text-3)", marginBottom: 14 }}>
        Describe a bug or a feature. The agent (Gemini 3.8 Flash or Claude Opus 4.8, your choice)
        plans it — you approve/edit the plan — then it builds it, runs a security review, and deploys
        to QC. You test on QC (hard-refresh), request changes if needed, then approve to push to prod.
        Every version is kept and any environment can be rolled back below. One request at a time.
      </p>

      {/* Submit form */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          background: "var(--surface)",
          border: "1px solid var(--border-soft)",
          borderRadius: 12,
          padding: 14,
          marginBottom: 18,
        }}
      >
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <select
            value={type}
            onChange={(e) => setType(e.target.value as DevRequestType)}
            style={inputStyle}
            title="Bug or feature"
          >
            <option value="bug">Bug</option>
            <option value="feature">Feature</option>
          </select>
          <select
            value={targetEnv}
            onChange={(e) => setTargetEnv(e.target.value as DevRequestTargetEnv)}
            style={inputStyle}
            title="Target environment (prod = bug fixes only)"
          >
            <option value="qc">Target: QC</option>
            <option value="prod" disabled={type === "feature"}>
              Target: Prod (bug fix)
            </option>
          </select>
          <select
            value={engine}
            onChange={(e) => setEngine(e.target.value as DevEngine)}
            style={inputStyle}
            title="Coding engine"
          >
            <option value="gemini-3.8-flash">Gemini 3.8 Flash</option>
            <option value="claude-opus-4.8">Claude Opus 4.8</option>
          </select>
          <input
            placeholder="Short title (optional)"
            value={title}
            maxLength={200}
            onChange={(e) => setTitle(e.target.value)}
            style={{ ...inputStyle, flex: 1, minWidth: 180 }}
          />
        </div>
        <textarea
          placeholder="Describe the bug or feature in detail — what you want, where, and how to tell it's done."
          value={description}
          maxLength={8000}
          rows={4}
          onChange={(e) => setDescription(e.target.value)}
          style={{ ...inputStyle, resize: "vertical" }}
        />
        <div>
          <button
            type="button"
            className="pill-btn primary"
            disabled={submitting || !description.trim()}
            onClick={submit}
          >
            {submitting ? "Filing…" : "File request"}
          </button>
        </div>
      </div>

      {/* Queue */}
      {requests.length === 0 ? (
        <div style={{ padding: 20, color: "var(--text-3)", fontSize: 13 }}>No requests yet.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {requests.map((r) => (
            <div
              key={r.id}
              style={{
                background: "var(--surface)",
                border: "1px solid var(--border-soft)",
                borderRadius: 10,
                padding: "12px 16px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    padding: "2px 6px",
                    borderRadius: 4,
                    background: "var(--surface-2)",
                    color: "var(--text-3)",
                  }}
                >
                  {r.type}
                </span>
                <span style={{ fontWeight: 600, fontSize: 14, flex: 1 }}>{r.title}</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: devStatusColor(r.status) }}>
                  {r.status}
                </span>
              </div>

              {/* Pipeline dots */}
              <div style={{ display: "flex", gap: 4, margin: "8px 0", flexWrap: "wrap" }}>
                {DEV_STATUS_FLOW.map((st) => {
                  const reached = DEV_STATUS_FLOW.indexOf(r.status) >= DEV_STATUS_FLOW.indexOf(st);
                  return (
                    <span
                      key={st}
                      title={st}
                      style={{
                        fontSize: 10,
                        padding: "1px 6px",
                        borderRadius: 999,
                        background: reached ? devStatusColor(r.status) : "var(--surface-2)",
                        color: reached ? "#fff" : "var(--text-4)",
                      }}
                    >
                      {st}
                    </span>
                  );
                })}
              </div>

              {/* Meta / attribution */}
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  flexWrap: "wrap",
                  fontSize: 11,
                  color: "var(--text-3)",
                  marginBottom: 6,
                }}
              >
                {r.targetEnv === "prod" && (
                  <span style={{ color: "var(--gc-red, #d93025)", fontWeight: 700 }}>PROD FIX</span>
                )}
                <span>engine: {r.engine ?? "gemini-3.8-flash"}</span>
                {r.createdBy && <span>· by {r.createdBy}</span>}
                {r.approvedBy && <span>· approved {r.approvedBy}</span>}
                {r.promotedBy && <span>· promoted {r.promotedBy}</span>}
                {r.gitSha && <span>· {r.gitSha}</span>}
              </div>

              <div
                dir="auto"
                style={{ fontSize: 13, color: "var(--text-2)", whiteSpace: "pre-wrap" }}
              >
                {r.description}
              </div>

              {r.error && (
                <div style={{ marginTop: 8, fontSize: 13, color: "var(--gc-red, #d93025)" }}>
                  Error: {r.error}
                </div>
              )}

              {/* Plan gate */}
              {r.status === "plan-ready" && r.plan && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>
                    Proposed plan
                  </div>
                  <pre
                    style={{
                      whiteSpace: "pre-wrap",
                      fontSize: 12,
                      background: "var(--surface-2)",
                      borderRadius: 8,
                      padding: "8px 10px",
                      maxHeight: 300,
                      overflowY: "auto",
                    }}
                  >
                    {r.plan}
                  </pre>
                  <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                    <button
                      type="button"
                      className="pill-btn primary"
                      disabled={busyId === r.id}
                      onClick={() => act(() => adminApi.approveDevPlan(r.id), r.id)}
                    >
                      Approve plan → build on QC
                    </button>
                    <button
                      type="button"
                      className="pill-btn"
                      disabled={busyId === r.id}
                      onClick={() => act(() => adminApi.rejectDevRequest(r.id), r.id)}
                    >
                      Reject
                    </button>
                  </div>
                  {/* Request plan changes → re-plan with feedback */}
                  <div style={{ marginTop: 8 }}>
                    <textarea
                      placeholder="Request plan changes (optional) — what to adjust before building…"
                      value={feedbackDraft[r.id] ?? ""}
                      maxLength={4000}
                      rows={2}
                      onChange={(e) =>
                        setFeedbackDraft((d) => ({ ...d, [r.id]: e.target.value }))
                      }
                      style={{ ...inputStyle, width: "100%", resize: "vertical" }}
                    />
                    <button
                      type="button"
                      className="ghost-btn xs"
                      style={{ marginTop: 4 }}
                      disabled={busyId === r.id}
                      onClick={() =>
                        act(async () => {
                          await adminApi.reviseDevPlan(r.id, (feedbackDraft[r.id] ?? "").trim());
                          setFeedbackDraft((d) => ({ ...d, [r.id]: "" }));
                        }, r.id)
                      }
                    >
                      Request plan changes → re-plan
                    </button>
                  </div>
                </div>
              )}

              {/* Security review summary (shown at QC + on a security block). */}
              {r.securityReview && (
                <div
                  style={{
                    marginTop: 10,
                    fontSize: 12,
                    background: "var(--surface-2)",
                    borderRadius: 8,
                    padding: "8px 10px",
                  }}
                >
                  <div style={{ fontWeight: 600 }}>
                    🔒 Security review:{" "}
                    <span
                      style={{
                        color:
                          r.securityReview.status === "blocked"
                            ? "var(--gc-red, #d93025)"
                            : "var(--gc-green, #137333)",
                      }}
                    >
                      {r.securityReview.status}
                    </span>{" "}
                    <span style={{ color: "var(--text-3)", fontWeight: 400 }}>
                      ({r.securityReview.highCount} high · {r.securityReview.mediumCount} med ·{" "}
                      {r.securityReview.lowCount} low; {r.securityReview.iterations} auto-fix pass
                      {r.securityReview.iterations === 1 ? "" : "es"})
                    </span>
                  </div>
                  {r.securityReview.findings.length > 0 && (
                    <ul style={{ margin: "4px 0 0", paddingLeft: 16, color: "var(--text-2)" }}>
                      {r.securityReview.findings.map((f, i) => (
                        <li key={i}>
                          <b>[{f.severity}]</b> {f.title}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {/* QC gate — review, request changes, or approve to prod. */}
              {r.status === "qc-deployed" && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 12, color: "var(--gc-green, #137333)", marginBottom: 4 }}>
                    ✅ Deployed to QC{r.gitSha ? ` (image ${r.gitSha})` : ""}. Test it, then approve
                    to push the same image to prod, or request changes.
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                    {r.qcUrl && (
                      <a
                        href={r.qcUrl}
                        target="_blank"
                        rel="noreferrer"
                        style={{ fontSize: 13, color: "var(--gc-blue)", textDecoration: "none" }}
                      >
                        ↗ Review on QC
                      </a>
                    )}
                    <button
                      type="button"
                      className="ghost-btn xs"
                      onClick={() => {
                        if (r.qcUrl) window.open(`${r.qcUrl}/?_=${Date.now()}`, "_blank");
                      }}
                      title="Open QC with a cache-busting reload"
                    >
                      ⟳ Hard-refresh QC
                    </button>
                  </div>
                  <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                    <button
                      type="button"
                      className="pill-btn primary"
                      disabled={busyId === r.id}
                      onClick={() =>
                        act(async () => {
                          const typed = window.prompt(
                            "Type 'promote' to push this tested image to PRODUCTION:",
                          );
                          if (typed !== "promote") {
                            toast.push({ tone: "info", message: "Promotion cancelled." });
                            return;
                          }
                          await adminApi.approveDevProd(r.id);
                        }, r.id)
                      }
                    >
                      Approve → push to prod
                    </button>
                    <button
                      type="button"
                      className="pill-btn"
                      disabled={busyId === r.id}
                      onClick={() => act(() => adminApi.rejectDevRequest(r.id), r.id)}
                    >
                      Reject
                    </button>
                  </div>
                  {/* Request build changes → re-build on the same branch */}
                  <div style={{ marginTop: 8 }}>
                    <textarea
                      placeholder="Request changes (optional) — what to fix/adjust, then re-deploy to QC…"
                      value={feedbackDraft[r.id] ?? ""}
                      maxLength={4000}
                      rows={2}
                      onChange={(e) =>
                        setFeedbackDraft((d) => ({ ...d, [r.id]: e.target.value }))
                      }
                      style={{ ...inputStyle, width: "100%", resize: "vertical" }}
                    />
                    <button
                      type="button"
                      className="ghost-btn xs"
                      style={{ marginTop: 4 }}
                      disabled={busyId === r.id}
                      onClick={() =>
                        act(async () => {
                          await adminApi.reviseDevBuild(r.id, (feedbackDraft[r.id] ?? "").trim());
                          setFeedbackDraft((d) => ({ ...d, [r.id]: "" }));
                        }, r.id)
                      }
                    >
                      Request changes → re-build on QC
                    </button>
                  </div>
                  {r.diff && (
                    <pre
                      style={{
                        whiteSpace: "pre",
                        fontSize: 11,
                        fontFamily: "var(--font-mono)",
                        background: "var(--surface-2)",
                        borderRadius: 8,
                        padding: "8px 10px",
                        maxHeight: 360,
                        overflow: "auto",
                        marginTop: 8,
                      }}
                    >
                      {r.diff}
                    </pre>
                  )}
                </div>
              )}

              {/* Live logs toggle */}
              <button
                type="button"
                className="ghost-btn xs"
                style={{ marginTop: 8 }}
                onClick={() => setSelected(selected === r.id ? null : r.id)}
              >
                {selected === r.id ? "Hide logs & activity" : "Show logs & activity"}
              </button>
              {selected === r.id && (
                <>
                  {comments.length > 0 && (
                    <div style={{ marginTop: 6, fontSize: 12, color: "var(--text-2)" }}>
                      <div style={{ fontWeight: 600, marginBottom: 2 }}>Feedback / comments</div>
                      {comments.map((c) => (
                        <div key={c.id} style={{ marginBottom: 2 }}>
                          <span style={{ color: "var(--text-3)" }}>
                            {c.authorEmail} ({c.kind}):
                          </span>{" "}
                          {c.text}
                        </div>
                      ))}
                    </div>
                  )}
                  <div
                    style={{
                      marginTop: 6,
                      fontSize: 11,
                      fontFamily: "var(--font-mono)",
                      color: "var(--text-3)",
                      maxHeight: 200,
                      overflowY: "auto",
                    }}
                  >
                    {logs.length === 0 ? (
                      <div>No logs yet.</div>
                    ) : (
                      logs.map((l) => (
                        <div key={l.id}>
                          [{l.phase}] {l.message}
                        </div>
                      ))
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Version history + rollback */}
      <div style={{ marginTop: 28 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>Version history</h3>
          <div className="seg seg-tabs">
            {(["qc", "prod"] as DevRequestTargetEnv[]).map((e) => (
              <button
                key={e}
                type="button"
                className={historyEnv === e ? "active" : ""}
                onClick={() => setHistoryEnv(e)}
              >
                {e.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
        <p style={{ fontSize: 12, color: "var(--text-3)", marginBottom: 8 }}>
          Every deploy/promote/rollback. Roll {historyEnv.toUpperCase()} back to any prior image —
          no rebuild.
        </p>
        {deployments.length === 0 ? (
          <div style={{ fontSize: 13, color: "var(--text-3)" }}>No deployments recorded yet.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {deployments.map((d, idx) => (
              <div
                key={d.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  flexWrap: "wrap",
                  fontSize: 12,
                  background: "var(--surface)",
                  border: "1px solid var(--border-soft)",
                  borderRadius: 8,
                  padding: "6px 10px",
                }}
              >
                <span style={{ fontFamily: "var(--font-mono)", fontWeight: 700 }}>{d.gitSha}</span>
                <span style={{ color: "var(--text-3)" }}>{d.action}</span>
                {idx === 0 && (
                  <span style={{ color: "var(--gc-green, #137333)", fontWeight: 700 }}>current</span>
                )}
                {d.title && <span style={{ flex: 1, color: "var(--text-2)" }}>{d.title}</span>}
                <span style={{ color: "var(--text-3)" }}>{d.deployedBy}</span>
                <span style={{ color: "var(--text-4)" }}>
                  {new Date(d.createdAt).toLocaleString()}
                </span>
                {idx !== 0 && (
                  <button
                    type="button"
                    className="ghost-btn xs"
                    disabled={busyId === d.id}
                    onClick={() =>
                      act(async () => {
                        const label = historyEnv.toUpperCase();
                        if (historyEnv === "prod") {
                          const typed = window.prompt(
                            `Type 'rollback' to roll PROD back to image ${d.gitSha}:`,
                          );
                          if (typed !== "rollback") return;
                        } else if (
                          !window.confirm(`Roll ${label} back to image ${d.gitSha}?`)
                        ) {
                          return;
                        }
                        await adminApi.rollbackDeployment(historyEnv, d.gitSha);
                        toast.push({ tone: "success", message: `Rolling ${label} back to ${d.gitSha}…` });
                      }, d.id)
                    }
                  >
                    Roll back to this
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function KnowledgeBaseSection() {
  const [docs, setDocs] = useState<DocumentRecord[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [linkUrl, setLinkUrl] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const googleToken = useAuthStore((s) => s.googleAccessToken);
  const setGoogleToken = useAuthStore((s) => s.setGoogleAccessToken);

  function reload() {
    adminApi
      .fetchDocuments()
      .then(setDocs)
      .catch(() => setDocs([]));
  }
  useEffect(() => {
    reload();
  }, []);

  async function onConnectDrive() {
    setErr(null);
    try {
      const token = await connectGoogleDrive();
      setGoogleToken(token);
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  async function onAddLink() {
    const url = linkUrl.trim();
    if (!url) return;
    // Google Docs/Drive links are read via the admin's Drive token; any other public
    // URL is fetched + extracted server-side (no Drive token needed).
    const isDriveUrl = /(?:docs|drive)\.google\.com/i.test(url);
    setBusy(true);
    setErr(null);
    try {
      let token = googleToken;
      if (isDriveUrl && !token) {
        token = await connectGoogleDrive();
        setGoogleToken(token);
      }
      try {
        await adminApi.addDocumentLink(url, isDriveUrl ? (token ?? undefined) : undefined);
      } catch (e) {
        // Drive token expired → reconnect once and retry.
        if (isDriveUrl && (e as { status?: number }).status === 401) {
          token = await connectGoogleDrive();
          setGoogleToken(token);
          await adminApi.addDocumentLink(url, token);
        } else {
          throw e;
        }
      }
      setLinkUrl("");
      reload();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function onPick(files: FileList | null) {
    if (!files || files.length === 0) return;
    setBusy(true);
    setErr(null);
    try {
      for (const f of Array.from(files)) {
        await adminApi.uploadDocument(f);
      }
      reload();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  async function onDelete(id: string) {
    if (!window.confirm("Delete this document from the knowledge base?")) return;
    await adminApi.deleteDocument(id).catch(() => {});
    reload();
  }

  return (
    <section style={{ marginTop: 28 }}>
      <h2
        style={{
          fontFamily: "var(--font-display)",
          fontSize: 18,
          fontWeight: 700,
          marginBottom: 4,
        }}
      >
        Knowledge base
      </h2>
      <p style={{ fontSize: 13, color: "var(--text-3)", marginBottom: 14 }}>
        Documents (PDF, Word, images) the assistant searches to ground live hints with citations.
      </p>

      <div style={{ marginBottom: 14 }}>
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.png,.jpg,.jpeg"
          multiple
          style={{ display: "none" }}
          onChange={(e) => onPick(e.target.files)}
        />
        <button
          type="button"
          className="pill-btn primary"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
        >
          {busy ? "Uploading & indexing…" : "+ Upload document"}
        </button>
        {err && (
          <span style={{ marginLeft: 12, color: "var(--gc-red, #d93025)", fontSize: 13 }}>
            {err}
          </span>
        )}
      </div>

      {/* Import an internal Google Doc by link (read via your Google account) */}
      <div
        style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          marginBottom: 18,
          flexWrap: "wrap",
        }}
      >
        <input
          type="url"
          value={linkUrl}
          placeholder="Paste a Google Doc / Drive link or any public URL…"
          onChange={(e) => setLinkUrl(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") onAddLink();
          }}
          style={{
            flex: "1 1 320px",
            font: "inherit",
            fontSize: 13,
            padding: "8px 10px",
            border: "1px solid var(--border-soft)",
            borderRadius: "var(--r-sm)",
            background: "var(--surface)",
            color: "var(--text-1)",
          }}
        />
        <button type="button" className="ghost-btn" disabled={busy} onClick={onAddLink}>
          {busy ? "Adding…" : "Add link"}
        </button>
        {!googleToken && (
          <button type="button" className="pill-btn" onClick={onConnectDrive}>
            Connect Google Drive
          </button>
        )}
      </div>

      {docs && docs.length > 0 ? (
        <div
          style={{
            background: "var(--surface)",
            border: "1px solid var(--border-soft)",
            borderRadius: 12,
            overflow: "hidden",
          }}
        >
          {docs.map((d) => (
            <div
              key={d.id}
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 110px 70px",
                alignItems: "center",
                gap: 8,
                padding: "10px 16px",
                borderTop: "1px solid var(--border-soft)",
                fontSize: 13,
              }}
            >
              <a
                href={safeHref(d.sourceUrl) ?? `/documents/${d.id}/file`}
                target="_blank"
                rel="noreferrer"
                style={{ color: "var(--text-1)", fontWeight: 600, textDecoration: "none" }}
              >
                {d.source === "gdrive" ? "🔗 " : ""}
                {d.name}
              </a>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color:
                    d.status === "ready"
                      ? "var(--gc-green)"
                      : d.status === "failed"
                        ? "var(--gc-red, #d93025)"
                        : "var(--gc-yellow)",
                }}
              >
                {d.status}
              </span>
              <button
                type="button"
                className="ghost-btn"
                style={{ color: "var(--gc-red, #d93025)", fontSize: 12 }}
                onClick={() => onDelete(d.id)}
              >
                Delete
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div
          style={{
            padding: 24,
            textAlign: "center",
            color: "var(--text-3)",
            background: "var(--surface)",
            border: "1px solid var(--border-soft)",
            borderRadius: 12,
          }}
        >
          {docs ? "No documents yet." : "Loading…"}
        </div>
      )}
    </section>
  );
}

function formatMinutes(mins: number): string {
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function UserMeetings({ uid }: { uid: string }) {
  const [meetings, setMeetings] = useState<AdminMeetingView[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    adminApi
      .fetchUserMeetings(uid)
      .then(setMeetings)
      .catch((err) => {
        setError((err as Error).message);
        setMeetings([]);
      });
  }, [uid]);

  if (error)
    return <div style={{ padding: "12px 20px", color: "var(--gc-red)" }}>Error: {error}</div>;
  if (!meetings)
    return <div style={{ padding: "12px 20px", color: "var(--text-4)" }}>Loading…</div>;
  if (meetings.length === 0)
    return <div style={{ padding: "12px 20px", color: "var(--text-4)" }}>No meetings found.</div>;

  return (
    <div style={{ padding: "8px 16px 16px", background: "var(--surface-2)" }}>
      {meetings.map((m) => (
        // Admins can open any user's summary (server allows admin reads). Opens in
        // a new tab so the admin table stays put. Interviews have their own
        // evaluation screen — route them there, not to the sales summary.
        <Link
          key={m.id}
          to={m.meetingType === "interview" ? "/interviews/$id/summary" : "/meetings/$id/summary"}
          params={{ id: m.id }}
          target="_blank"
          title={
            m.meetingType === "interview"
              ? "Open this interview's evaluation"
              : "Open this meeting's summary"
          }
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "10px 12px",
            borderRadius: 8,
            fontSize: 13,
            borderBottom: "1px solid var(--border-soft)",
            color: "inherit",
            textDecoration: "none",
            cursor: "pointer",
          }}
        >
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 12,
              color: "var(--text-3)",
              minWidth: 80,
            }}
          >
            {formatDate(m.createdAt)}
          </span>
          <span style={{ fontWeight: 600, minWidth: 100 }}>{m.client || "—"}</span>
          <span style={{ flex: 1, color: "var(--text-2)" }}>
            {m.title}
            {m.meetingType === "simulation" && (
              <span
                style={{
                  marginLeft: 6,
                  padding: "1px 5px",
                  borderRadius: 4,
                  fontSize: 10,
                  fontWeight: 600,
                  background: "#f3e8fd",
                  color: "#7627bb",
                }}
              >
                SIM
              </span>
            )}
            {m.meetingType === "interview" && (
              <span
                style={{
                  marginLeft: 6,
                  padding: "1px 5px",
                  borderRadius: 4,
                  fontSize: 10,
                  fontWeight: 600,
                  background: "#e6f4ea",
                  color: "#137333",
                }}
              >
                INTERVIEW
              </span>
            )}
          </span>
          {m.role && (
            <span
              style={{
                padding: "1px 6px",
                borderRadius: 4,
                fontSize: 10,
                fontWeight: 600,
                background: "var(--gc-blue-50, #e8f0fe)",
                color: "var(--gc-blue, #1a73e8)",
                minWidth: 0,
                whiteSpace: "nowrap",
              }}
              title="Meeting role"
            >
              {m.role}
            </span>
          )}
          <span
            style={{ fontSize: 12, color: "var(--text-3)", minWidth: 50 }}
            title="Meeting length"
          >
            {m.durationMinutes != null ? `${m.durationMinutes} min` : m.duration}
          </span>
          <span style={{ fontSize: 12, color: "var(--text-3)", minWidth: 96 }} title="Finished at">
            {m.endedAt
              ? `ended ${new Date(m.endedAt).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}`
              : "—"}
          </span>
          <span style={{ fontSize: 12, color: "var(--text-3)", minWidth: 70 }}>
            {m.status ?? "—"}
          </span>
        </Link>
      ))}
    </div>
  );
}

/** moMA profile URL for a @google.com address (…/person/<ldap>), else null. */
function momaUrl(email: string): string | null {
  const ldap = /^([^@]+)@google\.com$/i.exec((email ?? "").trim())?.[1];
  return ldap ? `https://moma.corp.google.com/person/${ldap.toLowerCase()}` : null;
}

/** A parsed row from a pasted moMA export (email is the match key). */
type MomaRow = { email: string; name: string; title: string; location: string };

/** Parse a moMA export pasted from the internal tool. Each data line is a table
 * row like: Name, Email, Business Title, …(Moma Profile). Anchors on the
 * @google.com email and takes the cell right after it as the Business Title.
 * Columns are split on tabs, falling back to runs of 2+ spaces when a copy loses
 * its tabs (so the trailing Moma-Profile name column doesn't leak into the title). */
function parseMomaExport(text: string): MomaRow[] {
  const clean = (s: string) =>
    (s ?? "")
      .replace(/open_in_new/gi, "")
      .replace(/↗/g, "")
      .trim();
  const emailRe = /[\w.+-]+@google\.com/i;
  const out: MomaRow[] = [];
  const seen = new Set<string>();
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) continue;
    // Skip the header row.
    if (/business title/i.test(line) && /email/i.test(line)) continue;
    const m = emailRe.exec(line);
    if (!m) continue;
    const email = m[0].toLowerCase();
    if (seen.has(email)) continue;

    let name = "";
    let title = "";
    let location = "";
    // Columns: prefer tabs; if the copy lost tabs, fall back to runs of 2+ spaces
    // (still separates cells) before a last-resort single-email split.
    let cols = line.split("\t").map(clean).filter(Boolean);
    if (cols.length < 3)
      cols = line
        .split(/\s{2,}/)
        .map(clean)
        .filter(Boolean);
    if (cols.length >= 3) {
      const emailIdx = cols.findIndex((c) => emailRe.test(c));
      // Columns after the email are Business Title, then Location; the trailing
      // Moma-Profile (repeated name) cell is separate, so guard against it.
      name = emailIdx > 0 ? clean(cols[0] ?? "") : "";
      title = clean(cols[emailIdx + 1] ?? "");
      const loc = clean(cols[emailIdx + 2] ?? "");
      // Ignore the moMA-profile column (which repeats the person's name).
      location = loc && loc.toLowerCase() !== name.toLowerCase() ? loc : "";
    } else {
      // Single blob: name is before the email, title is what follows it.
      name = clean(line.slice(0, m.index));
      title = clean(line.slice(m.index + m[0].length));
    }
    if (/^n\/?a$/i.test(title)) title = "";
    if (/^n\/?a$/i.test(location)) location = "";
    if (!name && !title && !location) continue;
    seen.add(email);
    out.push({ email, name, title, location });
  }
  return out;
}

/** Small "moMA ↗" link shown next to a user's name in the admin table. */
function MomaLink({ email }: { email: string }) {
  const url = momaUrl(email);
  if (!url) return null;
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      onClick={(e) => e.stopPropagation()}
      title={`Open ${email} in moMA`}
      style={{
        fontSize: 10.5,
        fontWeight: 600,
        color: "#1a73e8",
        background: "#e8f0fe",
        borderRadius: 999,
        padding: "1px 7px",
        textDecoration: "none",
        flexShrink: 0,
      }}
    >
      moMA ↗
    </a>
  );
}

/** Inline job-title editor (prefilled from the user's stored title). Saves on
 * Enter or the Save button. Pair it with the moMA link to look the title up. */
function TitleInput({
  user,
  onSave,
}: {
  user: AdminUser;
  onSave: (uid: string, title: string) => Promise<void>;
}) {
  const [val, setVal] = useState(user.title ?? "");
  const [saving, setSaving] = useState(false);
  const ref = useRef<HTMLTextAreaElement>(null);
  const dirty = val.trim() !== (user.title ?? "").trim();

  // Re-sync when the stored title changes externally (e.g. a bulk moMA apply).
  useEffect(() => setVal(user.title ?? ""), [user.title]);

  // Auto-grow so the full title is always visible (wraps to as many lines as
  // needed). Runs after each render so it tracks both typing and external updates.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  });

  async function commit() {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      await onSave(user.uid, val.trim());
    } finally {
      setSaving(false);
    }
  }
  return (
    <div
      style={{ display: "flex", gap: 6, alignItems: "flex-start", marginTop: 4 }}
      onClick={(e) => e.stopPropagation()}
    >
      <textarea
        ref={ref}
        value={val}
        rows={1}
        placeholder="Job title (open moMA →, copy it here)"
        onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => {
          // Enter saves; Shift+Enter inserts a newline.
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            void commit();
          }
        }}
        style={{
          flex: 1,
          minWidth: 0,
          fontSize: 12,
          lineHeight: 1.4,
          padding: "5px 8px",
          border: "1px solid var(--border)",
          borderRadius: 6,
          background: "var(--surface)",
          color: "var(--text-1)",
          fontFamily: "inherit",
          resize: "none",
          overflow: "hidden",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      />
      {dirty && (
        <button
          type="button"
          className="pill-btn sm"
          onClick={() => void commit()}
          disabled={saving}
          style={{ flexShrink: 0 }}
        >
          {saving ? "…" : "Save"}
        </button>
      )}
    </div>
  );
}

function StatusChip({ status }: { status?: UserStatus }) {
  // Undefined / approved = no chip (the default, keeps the table clean).
  if (!status || status === "approved") return null;
  const color = status === "blocked" ? "#d93025" : "#b06000";
  const bg = status === "blocked" ? "#fce8e6" : "#fef7e0";
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: ".04em",
        color,
        background: bg,
        borderRadius: 999,
        padding: "2px 7px",
        flexShrink: 0,
      }}
    >
      {status}
    </span>
  );
}

/** Small badge showing how the user obtained their API key. Highlights users who
 * indicated they have NO key so an admin can grant them a shared key. */
function ApiKeyBadge({ source }: { source?: AdminUser["apiKeySource"] }) {
  const map: Record<string, { label: string; color: string; bg: string }> = {
    personal: { label: "Own key", color: "#1e7e34", bg: "#e6f4ea" },
    shared: { label: "Shared key", color: "#1a73e8", bg: "#e8f0fe" },
    "managed-project": { label: "Own project", color: "#1a73e8", bg: "#e8f0fe" },
    none: { label: "No key", color: "#b06000", bg: "#fef7e0" },
  };
  const meta = source
    ? map[source]
    : { label: "Unknown", color: "var(--text-4)", bg: "var(--surface-2)" };
  if (!meta) return null;
  return (
    <span
      title="How this user obtained their API key"
      style={{
        fontSize: 9,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: ".03em",
        color: meta.color,
        background: meta.bg,
        borderRadius: 999,
        padding: "1px 6px",
        flexShrink: 0,
        alignSelf: "flex-start",
      }}
    >
      {meta.label}
    </span>
  );
}

type ManagedNote = ReleaseNote & { approved: boolean };

function WhatsNewApprovalsSection() {
  const [notes, setNotes] = useState<ManagedNote[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  // Per-note editable drafts (title + items-as-text).
  const [drafts, setDrafts] = useState<Record<string, { title: string; itemsText: string }>>({});

  function refresh() {
    adminApi
      .fetchWhatsNew()
      .then((ns) => {
        setNotes(ns);
        setDrafts(
          Object.fromEntries(
            ns.map((n) => [n.id, { title: n.title, itemsText: n.items.join("\n") }]),
          ),
        );
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }
  useEffect(refresh, []);

  async function save(id: string) {
    const d = drafts[id];
    if (!d) return;
    setBusyId(id);
    try {
      await adminApi.editRelease(id, {
        title: d.title,
        items: d.itemsText
          .split("\n")
          .map((s) => s.trim())
          .filter(Boolean),
      });
      refresh();
    } catch (err) {
      alert(`Save failed: ${(err as Error).message}`);
    } finally {
      setBusyId(null);
    }
  }

  async function toggle(id: string, approved: boolean) {
    setBusyId(id);
    try {
      await adminApi.setReleaseApproval(id, approved);
      refresh();
    } catch (err) {
      alert(`Failed: ${(err as Error).message}`);
    } finally {
      setBusyId(null);
    }
  }

  if (!loaded) return <p style={{ color: "var(--text-3)" }}>Loading…</p>;
  if (notes.length === 0)
    return <p style={{ color: "var(--text-3)" }}>No release notes awaiting review.</p>;

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "8px 10px",
    borderRadius: 8,
    border: "1px solid var(--border-1, #ddd)",
    background: "var(--surface-2, #fafafa)",
    fontSize: 13,
    marginBottom: 8,
  };

  return (
    <section>
      <h2 style={{ fontSize: 15, fontWeight: 700, margin: "0 0 4px" }}>
        “What's new” — review &amp; publish
      </h2>
      <p style={{ fontSize: 12, color: "var(--text-3)", margin: "0 0 16px" }}>
        New release notes stay hidden from users (popup + emails) until approved. You can edit the
        text before publishing.
      </p>
      {notes.map((n) => {
        const d = drafts[n.id] ?? { title: n.title, itemsText: n.items.join("\n") };
        return (
          <div
            key={n.id}
            style={{
              border: "1px solid var(--border-1, #e0e0e0)",
              borderRadius: 12,
              padding: 16,
              marginBottom: 14,
              background: "var(--surface)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
              <span
                style={{ fontSize: 12, color: "var(--text-3)", fontFamily: "var(--font-mono)" }}
              >
                v{n.version} · {n.date}
              </span>
              <span
                style={{ fontSize: 11, fontWeight: 700, color: n.approved ? "#34a853" : "#f9ab00" }}
              >
                {n.approved ? "Published" : "Pending"}
              </span>
            </div>
            <label style={{ fontSize: 12, color: "var(--text-3)" }}>Title</label>
            <input
              style={inputStyle}
              value={d.title}
              onChange={(e) =>
                setDrafts((p) => ({ ...p, [n.id]: { ...d, title: e.target.value } }))
              }
            />
            <label style={{ fontSize: 12, color: "var(--text-3)" }}>Items (one per line)</label>
            <textarea
              style={{ ...inputStyle, minHeight: 90, resize: "vertical" }}
              value={d.itemsText}
              onChange={(e) =>
                setDrafts((p) => ({ ...p, [n.id]: { ...d, itemsText: e.target.value } }))
              }
            />
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button
                type="button"
                className="pill-btn"
                disabled={busyId === n.id}
                onClick={() => save(n.id)}
              >
                Save text
              </button>
              <button
                type="button"
                className="pill-btn primary"
                disabled={busyId === n.id}
                onClick={() => toggle(n.id, !n.approved)}
              >
                {busyId === n.id ? "…" : n.approved ? "Unpublish" : "Approve & publish"}
              </button>
            </div>
          </div>
        );
      })}
    </section>
  );
}

function MarketplaceApprovalsSection() {
  const [tools, setTools] = useState<MarketplaceTool[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  function refresh() {
    adminApi
      .fetchPendingTools()
      .then(setTools)
      .catch(() => {})
      .finally(() => setLoaded(true));
  }
  useEffect(refresh, []);

  async function decide(id: string, approve: boolean) {
    setBusyId(id);
    try {
      if (approve) await adminApi.approveTool(id);
      else await adminApi.rejectTool(id);
      setTools((prev) => prev.filter((t) => t.id !== id));
    } catch (err) {
      alert(`Failed: ${(err as Error).message}`);
    } finally {
      setBusyId(null);
    }
  }

  if (!loaded) return <p style={{ color: "var(--text-3)" }}>Loading…</p>;
  if (tools.length === 0)
    return <p style={{ color: "var(--text-3)" }}>No tool suggestions awaiting review.</p>;

  return (
    <section>
      <h2 style={{ fontSize: 15, fontWeight: 700, margin: "0 0 4px" }}>
        Marketplace — review tool suggestions
      </h2>
      <p style={{ fontSize: 12, color: "var(--text-3)", margin: "0 0 16px" }}>
        User-suggested tools stay hidden from the Marketplace until you approve them.
      </p>
      {tools.map((t) => (
        <div
          key={t.id}
          style={{
            border: "1px solid var(--border-1, #e0e0e0)",
            borderRadius: 12,
            padding: 16,
            marginBottom: 14,
            background: "var(--surface)",
          }}
        >
          <div style={{ display: "flex", gap: 12, marginBottom: 4 }}>
            {t.hasImage && (
              <img
                src={`/api/marketplace/tools/${t.id}/image`}
                alt=""
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 10,
                  objectFit: "cover",
                  border: "1px solid var(--border-1, #e0e0e0)",
                  flex: "0 0 auto",
                }}
              />
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontWeight: 700, fontSize: 15 }}>{t.name}</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#5f6368" }}>
                  {t.audience} · {t.category}
                </span>
              </div>
              <p style={{ margin: "4px 0 8px", fontSize: 13, color: "var(--text-2)" }}>
                {t.description || <em style={{ color: "var(--text-3)" }}>No description</em>}
              </p>
            </div>
          </div>
          <div style={{ fontSize: 12, marginBottom: 10 }}>
            <a href={safeHref(t.url)} target="_blank" rel="noopener noreferrer">
              {t.url}
            </a>
          </div>
          {t.creatorName && (
            <div style={{ fontSize: 12, color: "var(--text-3)", marginBottom: 6 }}>
              Created by{" "}
              {t.creatorUrl ? (
                <a href={safeHref(t.creatorUrl)} target="_blank" rel="noopener noreferrer">
                  {t.creatorName}
                </a>
              ) : (
                <b>{t.creatorName}</b>
              )}
            </div>
          )}
          <div style={{ fontSize: 11, color: "var(--text-3)", marginBottom: 12 }}>
            Suggested by {t.createdByName} · {formatDate(t.createdAt)}
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button
              type="button"
              className="pill-btn"
              style={{ color: "var(--gc-red, #d93025)" }}
              disabled={busyId === t.id}
              onClick={() => decide(t.id, false)}
            >
              Reject
            </button>
            <button
              type="button"
              className="pill-btn primary"
              disabled={busyId === t.id}
              onClick={() => decide(t.id, true)}
            >
              {busyId === t.id ? "…" : "Approve"}
            </button>
          </div>
        </div>
      ))}
    </section>
  );
}

function PluginApprovalsSection() {
  const [plugins, setPlugins] = useState<MarketplaceTool[] | null>(null);
  const [approved, setApproved] = useState<MarketplaceTool[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  function refresh() {
    setLoadError(null);
    adminApi
      .fetchPendingPlugins()
      .then((list) => setPlugins(list))
      .catch((e) => {
        setPlugins([]);
        setLoadError((e as Error).message || "Failed to load");
      });
    adminApi
      .fetchApprovedPlugins()
      .then((list) => setApproved(list))
      .catch(() => setApproved([]));
  }
  useEffect(refresh, []);

  async function removePlugin(p: MarketplaceTool) {
    if (!confirm(`Remove "${p.name}" for everyone? Users will no longer see or run it.`)) return;
    setBusyId(p.id);
    try {
      await adminApi.removePlugin(p.id);
      refresh();
    } catch (err) {
      alert(`Failed: ${(err as Error).message}`);
    } finally {
      setBusyId(null);
    }
  }

  async function approve(p: MarketplaceTool) {
    setBusyId(p.id);
    try {
      await adminApi.approvePlugin(p.id, {
        dataScope: p.dataScope,
        actions: p.actions,
        surfaces: p.surfaces,
      });
      refresh();
    } catch (err) {
      alert(`Failed: ${(err as Error).message}`);
    } finally {
      setBusyId(null);
    }
  }
  async function act(id: string, fn: () => Promise<unknown>) {
    setBusyId(id);
    try {
      await fn();
      refresh();
    } catch (err) {
      alert(`Failed: ${(err as Error).message}`);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section style={{ marginTop: 28 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, margin: 0, flex: 1 }}>
          Plugin review pipeline{plugins ? ` (${plugins.length})` : ""}
        </h2>
        <button type="button" className="pill-btn" onClick={refresh}>
          Refresh
        </button>
      </div>
      <p style={{ fontSize: 12, color: "var(--text-3)", margin: "0 0 16px" }}>
        Plugins developers submit appear here after they pass the automated security scan. Approving
        adds the origin to the CSP allowlist and makes it enable-able by users.
      </p>
      {loadError && (
        <p style={{ color: "var(--gc-red,#d93025)", fontSize: 13, marginBottom: 12 }}>
          Couldn't load the review pipeline: {loadError}
        </p>
      )}
      {!plugins && !loadError && <p style={{ color: "var(--text-3)" }}>Loading…</p>}
      {plugins && plugins.length === 0 && !loadError && (
        <p style={{ color: "var(--text-3)" }}>No plugins in the review pipeline right now.</p>
      )}
      {(plugins ?? []).map((p) => {
        const rep = p.securityReport;
        const canApprove = p.reviewState === "awaiting-admin";
        return (
          <div
            key={p.id}
            style={{
              border: "1px solid var(--border-1, #e0e0e0)",
              borderRadius: 12,
              padding: 16,
              marginBottom: 14,
              background: "var(--surface)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
              <span style={{ fontWeight: 700, fontSize: 15 }}>{p.name}</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: "#5f6368" }}>
                {p.reviewState} · scope: {p.dataScope ?? "metadata"} ·{" "}
                {(p.surfaces ?? []).join(", ")}
              </span>
            </div>
            <div style={{ fontSize: 12, marginBottom: 8 }}>
              <a href={safeHref(p.embedUrl)} target="_blank" rel="noopener noreferrer">
                {p.embedUrl}
              </a>
            </div>
            <p style={{ margin: "0 0 8px", fontSize: 13, color: "var(--text-2)" }}>
              {p.description}
            </p>
            <div style={{ fontSize: 12, color: "var(--text-3)", marginBottom: 8 }}>
              Requested actions: {(p.actions ?? []).join(", ") || "none"}
            </div>
            {rep && (
              <div
                style={{
                  marginBottom: 10,
                  padding: 10,
                  borderRadius: 8,
                  background:
                    rep.verdict === "pass" ? "rgba(52,168,83,.08)" : "rgba(217,48,37,.08)",
                  border: `1px solid ${rep.verdict === "pass" ? "rgba(52,168,83,.4)" : "rgba(217,48,37,.4)"}`,
                }}
              >
                <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>
                  Security scan: {rep.verdict.toUpperCase()}
                </div>
                {rep.findings.length === 0 ? (
                  <div style={{ fontSize: 12, color: "var(--text-3)" }}>No findings.</div>
                ) : (
                  rep.findings.map((f, i) => (
                    <div key={i} style={{ fontSize: 12, marginBottom: 3 }}>
                      <b
                        style={{
                          color:
                            f.severity === "high"
                              ? "#d93025"
                              : f.severity === "medium"
                                ? "#b06000"
                                : "#5f6368",
                        }}
                      >
                        [{f.severity}]
                      </b>{" "}
                      {f.title} — {f.detail}
                    </div>
                  ))
                )}
              </div>
            )}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button
                type="button"
                className="pill-btn"
                disabled={busyId === p.id}
                onClick={() => act(p.id, () => adminApi.rescanPlugin(p.id))}
              >
                Re-scan
              </button>
              <button
                type="button"
                className="pill-btn"
                style={{ color: "var(--gc-red,#d93025)" }}
                disabled={busyId === p.id}
                onClick={() => act(p.id, () => adminApi.rejectTool(p.id))}
              >
                Reject
              </button>
              <button
                type="button"
                className="pill-btn primary"
                disabled={busyId === p.id || !canApprove}
                title={canApprove ? "" : "Must pass security review first"}
                onClick={() => approve(p)}
              >
                {busyId === p.id ? "…" : "Approve"}
              </button>
            </div>
          </div>
        );
      })}

      {/* Approved plugins live for everyone — admins can remove them here. */}
      <h3 style={{ fontSize: 14, fontWeight: 700, margin: "24px 0 4px" }}>
        Live plugins (approved for everyone){approved.length ? ` (${approved.length})` : ""}
      </h3>
      <p style={{ fontSize: 12, color: "var(--text-3)", margin: "0 0 12px" }}>
        Removing a plugin stops it rendering for all users and drops its origin from the CSP
        allowlist.
      </p>
      {approved.length === 0 ? (
        <p style={{ color: "var(--text-3)", fontSize: 13 }}>
          No plugins are live for everyone yet.
        </p>
      ) : (
        approved.map((p) => (
          <div
            key={p.id}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              border: "1px solid var(--border-soft)",
              borderRadius: 10,
              padding: "10px 12px",
              marginBottom: 8,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600 }}>{p.name}</div>
              <div style={{ fontSize: 12, color: "var(--text-3)" }}>{p.embedUrl}</div>
            </div>
            <button
              type="button"
              className="pill-btn"
              style={{ color: "var(--gc-red,#d93025)" }}
              disabled={busyId === p.id}
              onClick={() => void removePlugin(p)}
            >
              {busyId === p.id ? "Removing…" : "Remove"}
            </button>
          </div>
        ))
      )}
    </section>
  );
}

export function AdminScreen() {
  const nav = useNavigate();
  const email = useAuthStore((s) => s.email);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [analytics, setAnalytics] = useState<AdminAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedUid, setExpandedUid] = useState<string | null>(null);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteShared, setInviteShared] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [tab, setTab] = useState<"active" | "pending" | "blocked" | "all">("active");
  // Top-level admin page section tabs.
  const [adminTab, setAdminTab] = useState<
    | "users"
    | "mailer"
    | "whatsnew"
    | "knowledge"
    | "feedback"
    | "interview"
    | "report"
    | "voiceoffield"
    | "marketplace"
    | "bugs"
    | "devrequests"
  >("users");
  // Self-serve dev agent is available to the dev admins (Moshe + Avi).
  const isDevAdmin = isAdminEmail(email);
  const [query, setQuery] = useState("");
  // moMA copy/paste round-trip: copy a role-lookup prompt out, paste the answer back in.
  const [copied, setCopied] = useState<"" | "all" | "tab" | "missing">("");
  const [momaPaste, setMomaPaste] = useState("");
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState<string | null>(null);

  function copyEmails(scope: "all" | "tab" | "missing", list: AdminUser[]) {
    const emails = [...new Set(list.map((u) => u.email).filter(Boolean))];
    // Copy a ready-to-ask prompt (not just the addresses) so it can be pasted
    // straight into moMA / an assistant to get the roles back as a table.
    const prompt = `What is the job title (Business Title), office location and role of the following users? Reply as a table with columns: Name, Email, Business Title, Location, Moma Profile.\n\n${emails.join("\n")}`;
    navigator.clipboard?.writeText(prompt).then(
      () => {
        setCopied(scope);
        setTimeout(() => setCopied(""), 1800);
      },
      () => {},
    );
  }

  useEffect(() => {
    if (!isAdminEmail(email)) {
      nav({ to: "/dashboard" });
      return;
    }
    adminApi
      .fetchUsers()
      .then(setUsers)
      .catch((err) => {
        console.error("[admin] fetchUsers failed:", err);
        setError((err as Error).message);
        setUsers([]);
      })
      .finally(() => setLoading(false));
    adminApi
      .fetchInvites()
      .then(setInvites)
      .catch(() => {});
    adminApi
      .fetchAnalytics()
      .then(setAnalytics)
      .catch(() => {});
  }, [email, nav]);

  async function inviteUser() {
    const e = inviteEmail.trim().toLowerCase();
    if (!e || !e.includes("@")) {
      alert("Enter a valid email.");
      return;
    }
    setInviting(true);
    try {
      const inv = await adminApi.inviteUser(e, inviteShared);
      setInvites((prev) => [inv, ...prev.filter((i) => i.email !== e)]);
      // If they already requested access, the server just approved them — reflect it.
      setUsers((prev) =>
        prev.map((u) =>
          u.email.toLowerCase() === e
            ? { ...u, status: "approved", useSharedKey: inviteShared }
            : u,
        ),
      );
      setInviteEmail("");
      setInviteShared(false);
    } catch (err) {
      alert(`Invite failed: ${(err as Error).message}`);
    } finally {
      setInviting(false);
    }
  }

  async function changeStatus(uid: string, status: UserStatus) {
    try {
      await adminApi.setUserStatus(uid, status);
      setUsers((prev) => prev.map((u) => (u.uid === uid ? { ...u, status } : u)));
    } catch (err) {
      alert(`Failed to update user: ${(err as Error).message}`);
    }
  }

  async function changeTitle(uid: string, title: string) {
    setUsers((prev) => prev.map((u) => (u.uid === uid ? { ...u, title } : u)));
    try {
      await adminApi.setUserTitle(uid, title);
    } catch (err) {
      alert(`Failed to save title: ${(err as Error).message}`);
    }
  }

  async function applyMoma(rows: MomaRow[]) {
    if (rows.length === 0 || applying) return;
    setApplying(true);
    setApplyResult(null);
    try {
      const res = await adminApi.bulkUpdateProfiles(rows);
      // Optimistically patch the visible table (match by email) so it updates now.
      const byEmail = new Map(rows.map((r) => [r.email, r]));
      setUsers((prev) =>
        prev.map((u) => {
          const r = byEmail.get(u.email.toLowerCase());
          return r
            ? {
                ...u,
                name: r.name || u.name,
                title: r.title || u.title,
                location: r.location || u.location,
              }
            : u;
        }),
      );
      const suffix = res.unmatched.length
        ? ` · ${res.unmatched.length} unmatched (no account yet)`
        : "";
      setApplyResult(`Updated ${res.updated} user${res.updated === 1 ? "" : "s"}${suffix}`);
      setMomaPaste("");
      // Re-fetch from the server so the tables show the persisted names/titles.
      await adminApi
        .fetchUsers()
        .then(setUsers)
        .catch(() => {});
    } catch (err) {
      setApplyResult(`Failed: ${(err as Error).message}`);
    } finally {
      setApplying(false);
    }
  }

  async function changeSharedKey(uid: string, useSharedKey: boolean) {
    // optimistic
    setUsers((prev) => prev.map((u) => (u.uid === uid ? { ...u, useSharedKey } : u)));
    try {
      await adminApi.setUserSharedKey(uid, useSharedKey);
    } catch (err) {
      setUsers((prev) =>
        prev.map((u) => (u.uid === uid ? { ...u, useSharedKey: !useSharedKey } : u)),
      );
      alert(`Failed to update API-key setting: ${(err as Error).message}`);
    }
  }

  async function changeInterviewAccess(uid: string, interviewAccess: boolean) {
    // optimistic
    setUsers((prev) => prev.map((u) => (u.uid === uid ? { ...u, interviewAccess } : u)));
    try {
      await adminApi.setUserInterviewAccess(uid, interviewAccess);
    } catch (err) {
      setUsers((prev) =>
        prev.map((u) => (u.uid === uid ? { ...u, interviewAccess: !interviewAccess } : u)),
      );
      alert(`Failed to update interview access: ${(err as Error).message}`);
    }
  }

  async function changeTier(uid: string, tier: UserTier) {
    const prev = users.find((u) => u.uid === uid)?.tier ?? "regular";
    // optimistic
    setUsers((list) => list.map((u) => (u.uid === uid ? { ...u, tier } : u)));
    try {
      await adminApi.setUserTier(uid, tier);
    } catch (err) {
      setUsers((list) => list.map((u) => (u.uid === uid ? { ...u, tier: prev } : u)));
      alert(`Failed to update membership tier: ${(err as Error).message}`);
    }
  }

  async function changeHintPrefs(
    uid: string,
    prefs: { hintStrategy?: HintStrategy; comparisonMode?: "aggressive" | "conservative" },
  ) {
    let prevPrefs: { hintStrategy?: HintStrategy; comparisonMode?: "aggressive" | "conservative" } =
      {};
    setUsers((prev) =>
      prev.map((u) => {
        if (u.uid !== uid) return u;
        prevPrefs = { hintStrategy: u.hintStrategy, comparisonMode: u.comparisonMode };
        return { ...u, ...prefs };
      }),
    );
    try {
      await adminApi.setUserHintPrefs(uid, prefs);
    } catch (err) {
      setUsers((prev) => prev.map((u) => (u.uid === uid ? { ...u, ...prevPrefs } : u)));
      alert(`Failed to update coaching prefs: ${(err as Error).message}`);
    }
  }

  if (!isAdminEmail(email)) return null;

  const safeUsers = (users ?? []).filter((u) => !isSuperAdminEmail(u.email));
  const pending = safeUsers
    .filter((u) => u.status === "pending")
    // Most recent request first, so the newest requests are visible at the top.
    .sort((a, b) => (b.requestedAt ?? "").localeCompare(a.requestedAt ?? ""));
  const blocked = safeUsers.filter((u) => u.status === "blocked");
  const active = safeUsers.filter((u) => !u.status || u.status === "approved");
  const byTab =
    tab === "pending" ? pending : tab === "blocked" ? blocked : tab === "all" ? safeUsers : active;
  // Free-text filter on name / email / title (case-insensitive).
  const q = query.trim().toLowerCase();
  const shownUsers = q
    ? byTab.filter((u) => `${u.name ?? ""} ${u.email} ${u.title ?? ""}`.toLowerCase().includes(q))
    : byTab;
  const totalMeetings = safeUsers.reduce((s, u) => s + u.meetingCount, 0);
  const totalTime = safeUsers.reduce((s, u) => s + u.totalMinutes, 0);
  const totalSimulations = safeUsers.reduce((s, u) => s + u.simulationCount, 0);
  // Users we have no moMA data about yet — missing name, job title, OR office
  // location (the three fields the moMA lookup fills). This is the copy-to-moMA
  // target: only look up the people we're actually missing, never everyone.
  const usersMissing = safeUsers.filter(
    (u) => !u.name?.trim() || !u.title?.trim() || !u.location?.trim(),
  );
  // Active in the last N days (by lastActiveAt).
  const activeSince = (days: number) => {
    const cutoff = Date.now() - days * 86_400_000;
    return safeUsers.filter((u) => u.lastActiveAt && Date.parse(u.lastActiveAt) >= cutoff).length;
  };
  const avgMeetings = safeUsers.length ? (totalMeetings / safeUsers.length).toFixed(1) : "0";
  // Role distribution (Cloud: real roles; Ads: single role → fall back to job title).
  const roleDist = (() => {
    const useRole = ALL_ROLES.length > 1;
    const counts = new Map<string, number>();
    for (const u of safeUsers) {
      const key = (useRole ? u.role : u.title?.trim()) || "Unknown";
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return {
      byRole: useRole,
      items: [...counts.entries()]
        .sort((a, b) => b[1] - a[1])
        .map(([label, count]) => ({
          label,
          count,
        })),
    };
  })();

  const locationDist = (() => {
    const counts = new Map<string, number>();
    for (const u of safeUsers) {
      const key = u.location?.trim() || "Unknown";
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([label, count]) => ({ label, count }));
  })();
  // Users grouped by their moMA job title (Business Title).
  const titleDist = (() => {
    const counts = new Map<string, number>();
    for (const u of safeUsers) {
      const key = u.title?.trim() || "Unknown";
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([label, count]) => ({ label, count }));
  })();
  // Users grouped into broad CATEGORIES from their moMA role/title. Order matters:
  // "Sales Manager" is Sales (not Management); VP/Director are Management; anything
  // technical (CE / architect / AI / SE) is Customer Engineering.
  const categoryDist = (() => {
    const categorize = (raw: string | undefined): string => {
      const t = (raw ?? "").toLowerCase();
      if (!t.trim()) return "Unknown";
      if (/sales|seller|account exec|field sales|\bfsr\b|account manager/.test(t)) return "Sales";
      if (
        /\bvp\b|vice president|director|head of|chief|\bcxo\b|\bceo\b|\bcto\b|manager|\blead\b/.test(
          t,
        )
      )
        return "Management";
      if (
        /customer engineer|\bce\b|architect|\bai\b|solutions? eng|\bse\b|cloud eng|technical/.test(
          t,
        )
      )
        return "Customer Engineering";
      return "Other";
    };
    const counts = new Map<string, number>();
    for (const u of safeUsers) {
      const key = categorize(u.title);
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([label, count]) => ({ label, count }));
  })();

  // moMA paste → parsed rows + match preview against this env's users (by email).
  const parsedRows = parseMomaExport(momaPaste);
  const emailSet = new Set(safeUsers.map((u) => u.email.toLowerCase()));
  const matchedCount = parsedRows.filter((r) => emailSet.has(r.email)).length;
  const unmatchedEmails = parsedRows.filter((r) => !emailSet.has(r.email)).map((r) => r.email);

  return (
    <div className="dash">
      <DashHeader />

      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "22px 28px 60px" }}>
        <h1
          style={{
            fontFamily: "var(--font-display)",
            fontSize: 22,
            fontWeight: 700,
            marginBottom: 6,
          }}
        >
          Admin Dashboard
        </h1>
        <p style={{ fontSize: 13, color: "var(--text-3)", marginBottom: 16 }}>
          All users and their system usage
        </p>

        {/* Top-level tabs so the page isn't one long scroll. */}
        <div className="seg seg-tabs" style={{ marginBottom: 20, flexWrap: "wrap" }}>
          {(
            [
              ["users", "Users"],
              ["whatsnew", "What's new"],
              ["knowledge", "Knowledge base"],
              ["feedback", "Feedback"],
              ["report", "Daily report"],
              ["voiceoffield", "Voice of the field"],
              ["bugs", "User bugs"],
              ["interview", "Interview data"],
              ...(isDevAdmin ? [["devrequests", "Dev agent"] as const] : []),
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={adminTab === key ? "on" : ""}
              onClick={() => setAdminTab(key)}
            >
              {label}
            </button>
          ))}
        </div>

        {adminTab === "mailer" && <MailerSettingsSection />}

        {adminTab === "whatsnew" && <WhatsNewApprovalsSection />}

        {adminTab === "interview" && (
          <button
            type="button"
            className="pill-btn primary"
            onClick={() => nav({ to: "/interview-admin" })}
          >
            Manage interview data (AIMS) →
          </button>
        )}

        {adminTab === "users" && (
          <>
            {/* Access requests — new users awaiting approval */}
            {!loading && pending.length > 0 && (
              <section
                style={{
                  background: "var(--surface)",
                  border: "1px solid var(--gc-yellow, #f9ab00)",
                  borderRadius: 12,
                  boxShadow: "var(--sh-1)",
                  overflow: "hidden",
                  marginBottom: 20,
                }}
              >
                <div
                  style={{
                    padding: "12px 16px",
                    fontWeight: 700,
                    fontSize: 14,
                    background: "var(--gc-yellow-50, #fef7e0)",
                    borderBottom: "1px solid var(--border-soft)",
                  }}
                >
                  ⏳ Access requests ({pending.length})
                </div>
                {/* Cap the height so a long queue doesn't dominate the page — scrolls
                internally, newest requests first. */}
                <div style={{ maxHeight: 340, overflowY: "auto" }}>
                  {pending.map((u) => (
                    <div
                      key={u.uid}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        padding: "12px 16px",
                        borderTop: "1px solid var(--border-soft)",
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            fontWeight: 600,
                            fontSize: 13,
                          }}
                        >
                          {u.name || u.email.split("@")[0]}
                          <MomaLink email={u.email} />
                        </div>
                        <div
                          style={{
                            fontSize: 12,
                            color: "var(--text-3)",
                            fontFamily: "var(--font-mono)",
                          }}
                        >
                          {u.email}
                        </div>
                        <TitleInput user={u} onSave={changeTitle} />
                      </div>
                      <span style={{ fontSize: 12, color: "var(--text-4)", minWidth: 90 }}>
                        {u.requestedAt ? formatDate(u.requestedAt) : ""}
                      </span>
                      <button
                        type="button"
                        className="pill-btn primary"
                        onClick={() => changeStatus(u.uid, "approved")}
                      >
                        Approve
                      </button>
                      <button
                        type="button"
                        className="pill-btn"
                        onClick={() => changeStatus(u.uid, "blocked")}
                      >
                        Block
                      </button>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Invite users — pre-approve emails so they skip the request queue */}
            <section
              style={{
                background: "var(--surface)",
                border: "1px solid var(--border-soft)",
                borderRadius: 12,
                boxShadow: "var(--sh-1)",
                padding: "14px 16px",
                marginBottom: 20,
              }}
            >
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>Invite users</div>
              <div style={{ fontSize: 12, color: "var(--text-3)", marginBottom: 10 }}>
                Invited users are pre-approved immediately and receive an invitation email — no approval request needed.
              </div>
              <div style={{ display: "flex", gap: 8, marginBottom: invites.length ? 12 : 0 }}>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") void inviteUser();
                  }}
                  placeholder="user@company.com"
                  spellCheck={false}
                  style={{
                    flex: 1,
                    padding: "8px 12px",
                    borderRadius: 8,
                    border: "1px solid var(--border)",
                    fontSize: 13,
                  }}
                />
                <button
                  type="button"
                  className="pill-btn primary"
                  onClick={() => void inviteUser()}
                  disabled={inviting || !inviteEmail.trim()}
                >
                  {inviting ? "Inviting…" : "Invite"}
                </button>
              </div>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  fontSize: 12,
                  color: "var(--text-2)",
                  margin: "10px 0 2px",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={inviteShared}
                  onChange={(e) => setInviteShared(e.target.checked)}
                />
                <span>
                  Use <strong>our shared API key</strong> (VIP) — this user won&apos;t need to
                  create their own Gemini key.
                </span>
              </label>
              {/* The list of existing invites is intentionally not shown — admins can
              still send invites above, but past invite records aren't displayed. */}
            </section>

            {/* Stats bar */}
            {!loading && safeUsers.length > 0 && (
              <div style={{ display: "flex", gap: 16, marginBottom: 20, flexWrap: "wrap" }}>
                {[
                  { label: "Total users", value: safeUsers.length },
                  { label: "Active last 7d", value: activeSince(7) },
                  { label: "Active last 30d", value: activeSince(30) },
                  { label: "Total meetings", value: totalMeetings },
                  { label: "Avg meetings / user", value: avgMeetings },
                  { label: "Simulations", value: totalSimulations },
                  { label: "Total time", value: formatMinutes(totalTime) },
                  { label: "Missing moMA data", value: usersMissing.length },
                ].map((s) => (
                  <div
                    key={s.label}
                    style={{
                      flex: "1 1 150px",
                      minWidth: 150,
                      background: "var(--surface)",
                      border: "1px solid var(--border-soft)",
                      borderRadius: 10,
                      padding: "14px 18px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 11,
                        textTransform: "uppercase",
                        letterSpacing: ".06em",
                        color: "var(--text-4)",
                        marginBottom: 4,
                      }}
                    >
                      {s.label}
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: "var(--text-1)" }}>
                      {s.value}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Full-width daily strip: meetings per day over the last 30 days. Its own
                row above the grid so all 30 bars get horizontal room to breathe. */}
            {!loading && safeUsers.length > 0 && (
              <section
                style={{
                  background: "var(--surface)",
                  border: "1px solid var(--border-soft)",
                  borderRadius: 12,
                  boxShadow: "var(--sh-1)",
                  padding: "14px 16px",
                  marginBottom: 16,
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10 }}>
                  Meetings per day (last 30 days)
                </div>
                <MeetingsDailyBarChart data={analytics?.meetingsByDay ?? []} />
              </section>
            )}

            {/* Usage charts: meetings over time + user distribution */}
            {!loading && safeUsers.length > 0 && (
              <div style={{ display: "flex", gap: 16, marginBottom: 20, flexWrap: "wrap" }}>
                <section
                  style={{
                    flex: "1 1 420px",
                    minWidth: 320,
                    background: "var(--surface)",
                    border: "1px solid var(--border-soft)",
                    borderRadius: 12,
                    boxShadow: "var(--sh-1)",
                    padding: "14px 16px",
                  }}
                >
                  <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10 }}>
                    Meetings over time (monthly)
                  </div>
                  <MeetingsLineChart data={analytics?.meetingsByMonth ?? []} />
                </section>
                <section
                  style={{
                    flex: "1 1 420px",
                    minWidth: 320,
                    background: "var(--surface)",
                    border: "1px solid var(--border-soft)",
                    borderRadius: 12,
                    boxShadow: "var(--sh-1)",
                    padding: "14px 16px",
                  }}
                >
                  <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10 }}>
                    {roleDist.byRole ? "Users by role" : "Users by job title"}
                  </div>
                  <RolesBarChart data={roleDist.items} />
                </section>
                <section
                  style={{
                    flex: "1 1 420px",
                    minWidth: 320,
                    background: "var(--surface)",
                    border: "1px solid var(--border-soft)",
                    borderRadius: 12,
                    boxShadow: "var(--sh-1)",
                    padding: "14px 16px",
                  }}
                >
                  <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10 }}>
                    Users by location
                  </div>
                  <RolesBarChart data={locationDist} />
                </section>
                <section
                  style={{
                    flex: "1 1 420px",
                    minWidth: 320,
                    background: "var(--surface)",
                    border: "1px solid var(--border-soft)",
                    borderRadius: 12,
                    boxShadow: "var(--sh-1)",
                    padding: "14px 16px",
                  }}
                >
                  <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10 }}>
                    Users by moMA role
                  </div>
                  <RolesBarChart data={titleDist} />
                </section>
                <section
                  style={{
                    flex: "1 1 420px",
                    minWidth: 320,
                    background: "var(--surface)",
                    border: "1px solid var(--border-soft)",
                    borderRadius: 12,
                    boxShadow: "var(--sh-1)",
                    padding: "14px 16px",
                  }}
                >
                  <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10 }}>
                    Users by category
                  </div>
                  <RolesBarChart data={categoryDist} />
                </section>
                <section
                  style={{
                    flex: "1 1 420px",
                    minWidth: 320,
                    background: "var(--surface)",
                    border: "1px solid var(--border-soft)",
                    borderRadius: 12,
                    boxShadow: "var(--sh-1)",
                    padding: "14px 16px",
                  }}
                >
                  <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10 }}>
                    Meetings by type
                  </div>
                  <RolesBarChart data={analytics?.meetingsByType ?? []} />
                </section>
              </div>
            )}

            {error && (
              <div
                style={{
                  padding: "12px 16px",
                  marginBottom: 16,
                  borderRadius: 8,
                  background: "#fce8e6",
                  color: "#d93025",
                  fontSize: 13,
                }}
              >
                Error loading users: {error}
              </div>
            )}

            {/* Job titles from moMA — copy a role-lookup prompt, ask moMA agents, paste the answer back. */}
            {!loading && safeUsers.length > 0 && (
              <section
                style={{
                  background: "var(--surface)",
                  border: "1px solid var(--border-soft)",
                  borderRadius: 12,
                  boxShadow: "var(--sh-1)",
                  padding: "14px 16px",
                  marginBottom: 16,
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>
                  Job titles from moMA
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: "var(--text-3)",
                    marginBottom: 12,
                    lineHeight: 1.5,
                  }}
                >
                  <strong>1.</strong> Copy the ready-made prompt (a “what’s the job title of…”
                  question with the emails). <strong>2.</strong> Open{" "}
                  <a
                    href="https://moma.corp.google.com/agents"
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: "#1a73e8", fontWeight: 600 }}
                  >
                    moma.corp.google.com/agents
                  </a>
                  , paste the user info there, and copy the answer table it returns.{" "}
                  <strong>3.</strong> Paste that answer below and Apply — each row is matched by
                  email and saves that user’s full name, Business Title and Location. You can also
                  edit any title inline below (click <strong>moMA ↗</strong>).
                </div>
                <div>
                  <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
                    <button
                      type="button"
                      className="pill-btn"
                      onClick={() => copyEmails("missing", usersMissing)}
                      disabled={usersMissing.length === 0}
                      title="Only the users we have no moMA data about yet (missing name, title or location) — so you look up just the ones we're missing, not everyone"
                    >
                      {copied === "missing"
                        ? "Copied ✓"
                        : usersMissing.length === 0
                          ? "All users have moMA data ✓"
                          : `Copy prompt — needs moMA data (${usersMissing.length})`}
                    </button>
                    <button
                      type="button"
                      className="ghost-btn"
                      onClick={() => copyEmails("tab", shownUsers)}
                      title="Copy just the users currently shown in this tab"
                    >
                      {copied === "tab"
                        ? "Copied ✓"
                        : `Copy prompt — ${tab} tab (${shownUsers.length})`}
                    </button>
                  </div>
                  <textarea
                    value={momaPaste}
                    onChange={(e) => {
                      setMomaPaste(e.target.value);
                      setApplyResult(null);
                    }}
                    placeholder="Paste moMA’s export table here (Name, Email, Business Title, Location, …)"
                    rows={5}
                    spellCheck={false}
                    style={{
                      width: "100%",
                      boxSizing: "border-box",
                      fontSize: 12,
                      fontFamily: "var(--font-mono)",
                      padding: "8px 10px",
                      border: "1px solid var(--border-soft)",
                      borderRadius: 6,
                      background: "var(--surface)",
                      color: "var(--text-1)",
                      resize: "vertical",
                    }}
                  />
                  <div
                    style={{
                      display: "flex",
                      gap: 12,
                      alignItems: "center",
                      marginTop: 8,
                      flexWrap: "wrap",
                    }}
                  >
                    <button
                      type="button"
                      className="pill-btn primary"
                      disabled={applying || matchedCount === 0}
                      onClick={() =>
                        void applyMoma(parsedRows.filter((r) => emailSet.has(r.email)))
                      }
                    >
                      {applying
                        ? "Applying…"
                        : `Apply ${matchedCount} match${matchedCount === 1 ? "" : "es"}`}
                    </button>
                    {parsedRows.length > 0 && (
                      <span style={{ fontSize: 12, color: "var(--text-3)" }}>
                        {parsedRows.length} row{parsedRows.length === 1 ? "" : "s"} parsed ·{" "}
                        {matchedCount} matched
                        {unmatchedEmails.length > 0 && ` · ${unmatchedEmails.length} unmatched`}
                      </span>
                    )}
                    {applyResult && (
                      <span
                        style={{ fontSize: 12, color: "var(--gc-green, #188038)", fontWeight: 600 }}
                      >
                        {applyResult}
                      </span>
                    )}
                  </div>
                  {unmatchedEmails.length > 0 && (
                    <div style={{ fontSize: 11, color: "var(--text-4)", marginTop: 6 }}>
                      No account yet (skipped): {unmatchedEmails.slice(0, 8).join(", ")}
                      {unmatchedEmails.length > 8 && ` +${unmatchedEmails.length - 8} more`}
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* Users tabs: active / pending / blocked / all + search */}
            {!loading && safeUsers.length > 0 && (
              <div
                style={{
                  display: "flex",
                  gap: 12,
                  alignItems: "center",
                  marginBottom: 12,
                  flexWrap: "wrap",
                }}
              >
                <div className="seg seg-tabs" style={{ margin: 0 }}>
                  <button
                    type="button"
                    className={tab === "active" ? "on" : ""}
                    onClick={() => setTab("active")}
                  >
                    Active <span className="seg-count">{active.length}</span>
                  </button>
                  <button
                    type="button"
                    className={tab === "pending" ? "on" : ""}
                    onClick={() => setTab("pending")}
                  >
                    Pending <span className="seg-count">{pending.length}</span>
                  </button>
                  <button
                    type="button"
                    className={tab === "blocked" ? "on" : ""}
                    onClick={() => setTab("blocked")}
                  >
                    Blocked <span className="seg-count">{blocked.length}</span>
                  </button>
                  <button
                    type="button"
                    className={tab === "all" ? "on" : ""}
                    onClick={() => setTab("all")}
                  >
                    All <span className="seg-count">{safeUsers.length}</span>
                  </button>
                </div>
                <div style={{ position: "relative", flex: "1 1 220px", minWidth: 180 }}>
                  <input
                    type="search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search by name or email…"
                    spellCheck={false}
                    style={{
                      width: "100%",
                      boxSizing: "border-box",
                      fontSize: 13,
                      padding: "8px 12px",
                      borderRadius: 999,
                      border: "1px solid var(--border)",
                      background: "var(--surface)",
                      color: "var(--text-1)",
                    }}
                  />
                </div>
              </div>
            )}

            <section
              style={{
                background: "var(--surface)",
                border: "1px solid var(--border-soft)",
                borderRadius: 12,
                boxShadow: "var(--sh-1)",
                overflow: "hidden",
              }}
            >
              {loading ? (
                <div style={{ padding: 40, textAlign: "center", color: "var(--text-3)" }}>
                  Loading users…
                </div>
              ) : safeUsers.length === 0 && !error ? (
                <div style={{ padding: 40, textAlign: "center", color: "var(--text-3)" }}>
                  No users found.
                </div>
              ) : shownUsers.length === 0 ? (
                <div style={{ padding: 40, textAlign: "center", color: "var(--text-3)" }}>
                  {q ? `No users match “${query.trim()}”.` : `No ${tab} users.`}
                </div>
              ) : (
                <>
                  {/* Header */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr 80px 70px 70px 110px 130px 30px",
                      alignItems: "center",
                      padding: "10px 16px",
                      fontSize: 10.5,
                      textTransform: "uppercase",
                      letterSpacing: ".08em",
                      color: "var(--text-4)",
                      fontWeight: 600,
                      background: "var(--surface-2)",
                      borderBottom: "1px solid var(--border-soft)",
                    }}
                  >
                    <div>User</div>
                    <div>Email</div>
                    <div style={{ textAlign: "center" }}>Meetings</div>
                    <div style={{ textAlign: "center" }}>Sims</div>
                    <div style={{ textAlign: "center" }}>Time</div>
                    <div style={{ textAlign: "center" }}>Shared key</div>
                    <div>Last connected</div>
                    <div></div>
                  </div>

                  {/* Rows */}
                  {shownUsers.map((u) => {
                    const isExpanded = expandedUid === u.uid;
                    return (
                      <div key={u.uid} style={{ borderTop: "1px solid var(--border-soft)" }}>
                        <div
                          onClick={() => setExpandedUid(isExpanded ? null : u.uid)}
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr 80px 70px 70px 110px 130px 30px",
                            alignItems: "center",
                            padding: "12px 16px",
                            cursor: "pointer",
                            background: isExpanded ? "var(--surface-2)" : "transparent",
                            transition: "background .15s",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 4,
                              minWidth: 0,
                              paddingRight: 12,
                            }}
                          >
                            {/* Top row: avatar, name, moMA link, status, actions.
                            Wraps so the badges/buttons flow to a second line
                            instead of squeezing the name. */}
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                flexWrap: "wrap",
                                gap: 8,
                                minWidth: 0,
                              }}
                            >
                              <div
                                style={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: 8,
                                  background: u.status === "blocked" ? "#d93025" : "#4285F4",
                                  color: "white",
                                  fontWeight: 600,
                                  fontSize: 13,
                                  display: "grid",
                                  placeItems: "center",
                                  flexShrink: 0,
                                }}
                              >
                                {(u.name || u.email)?.[0]?.toUpperCase() || <UserIcon size={16} />}
                              </div>
                              <span
                                style={{
                                  fontWeight: 600,
                                  fontSize: 13,
                                  color: "var(--text-1)",
                                  // Full name, wrapping if needed so it's never cut.
                                  whiteSpace: "normal",
                                  overflowWrap: "anywhere",
                                  flex: "1 1 auto",
                                  minWidth: 0,
                                }}
                              >
                                {u.name || u.email.split("@")[0]}
                              </span>
                              <MomaLink email={u.email} />
                              <StatusChip status={u.status} />
                              {u.status === "pending" && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    changeStatus(u.uid, "approved");
                                  }}
                                  style={{
                                    fontSize: 11,
                                    padding: "2px 8px",
                                    borderRadius: 999,
                                    border: "1px solid var(--gc-blue, #1a73e8)",
                                    background: "var(--gc-blue, #1a73e8)",
                                    color: "#fff",
                                    cursor: "pointer",
                                    flexShrink: 0,
                                  }}
                                >
                                  Approve
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  changeStatus(
                                    u.uid,
                                    u.status === "blocked" ? "approved" : "blocked",
                                  );
                                }}
                                style={{
                                  fontSize: 11,
                                  padding: "2px 8px",
                                  borderRadius: 999,
                                  border: "1px solid var(--border)",
                                  background: "var(--surface)",
                                  cursor: "pointer",
                                  flexShrink: 0,
                                }}
                              >
                                {u.status === "blocked" ? "Unblock" : "Block"}
                              </button>
                            </div>
                            {/* API-key indicator — a small tag under the name so it
                            doesn't crowd/cut the name row. */}
                            <ApiKeyBadge source={u.apiKeySource} />
                            {/* Full-width title editor so long roles are readable */}
                            <TitleInput user={u} onSave={changeTitle} />
                          </div>
                          <div
                            style={{
                              fontSize: 12,
                              color: "var(--text-2)",
                              fontFamily: "var(--font-mono)",
                            }}
                          >
                            {u.email}
                          </div>
                          <div style={{ textAlign: "center", fontWeight: 600, fontSize: 15 }}>
                            {u.meetingCount}
                          </div>
                          <div
                            style={{ textAlign: "center", fontSize: 13, color: "var(--text-3)" }}
                          >
                            {u.simulationCount}
                          </div>
                          <div
                            style={{
                              textAlign: "center",
                              fontSize: 13,
                              fontWeight: 500,
                              color: "var(--text-2)",
                            }}
                          >
                            {formatMinutes(u.totalMinutes)}
                          </div>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              gap: 4,
                            }}
                          >
                            <label
                              onClick={(e) => e.stopPropagation()}
                              title="Allow this user to use our shared Gemini API key (no setup needed)"
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                                fontSize: 11,
                                color: u.useSharedKey ? "#1a73e8" : "var(--text-4)",
                                cursor: "pointer",
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={!!u.useSharedKey}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  changeSharedKey(u.uid, e.target.checked);
                                }}
                              />
                              Key: {u.useSharedKey ? "On" : "Off"}
                            </label>
                            <label
                              onClick={(e) => e.stopPropagation()}
                              title="Allow this user to access the Interview (AIMS) section"
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                                fontSize: 11,
                                color: u.interviewAccess ? "#7627bb" : "var(--text-4)",
                                cursor: "pointer",
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={!!u.interviewAccess}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  changeInterviewAccess(u.uid, e.target.checked);
                                }}
                              />
                              AIMS: {u.interviewAccess ? "On" : "Off"}
                            </label>
                            <label
                              onClick={(e) => e.stopPropagation()}
                              title="Membership tier. Gold is awarded manually; Silver is earned when a user submits feedback."
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                                fontSize: 11,
                                color:
                                  u.tier === "gold"
                                    ? "#b8860b"
                                    : u.tier === "silver"
                                      ? "#8a8f98"
                                      : "var(--text-4)",
                                cursor: "pointer",
                              }}
                            >
                              Tier:
                              <select
                                value={u.tier ?? "regular"}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  changeTier(u.uid, e.target.value as UserTier);
                                }}
                                style={{ fontSize: 11 }}
                              >
                                <option value="gold">🥇 Gold</option>
                                <option value="silver">🥈 Silver</option>
                                <option value="regular">Regular</option>
                              </select>
                            </label>
                          </div>
                          <div
                            style={{
                              fontSize: 12,
                              fontFamily: "var(--font-mono)",
                              color: "var(--text-3)",
                            }}
                          >
                            {formatDate(u.lastActiveAt ?? u.lastMeetingDate ?? "")}
                          </div>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "center",
                              transform: isExpanded ? "rotate(180deg)" : "rotate(0)",
                              transition: "transform .2s",
                            }}
                          >
                            <Chev size={14} />
                          </div>
                        </div>
                        {isExpanded && (
                          <>
                            <div
                              style={{
                                display: "flex",
                                gap: 20,
                                flexWrap: "wrap",
                                padding: "12px 16px",
                                borderTop: "1px solid var(--border-soft, #eee)",
                              }}
                            >
                              <label
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 4,
                                  fontSize: 12,
                                  color: "var(--text-2)",
                                }}
                              >
                                Hint strategy
                                <select
                                  value={u.hintStrategy ?? "live"}
                                  onChange={(e) =>
                                    changeHintPrefs(u.uid, {
                                      hintStrategy: e.target.value as HintStrategy,
                                    })
                                  }
                                  style={{
                                    padding: "6px 10px",
                                    borderRadius: 8,
                                    border: "1px solid var(--border)",
                                    fontSize: 13,
                                  }}
                                >
                                  <option value="live">Live</option>
                                  <option value="balanced">Balanced</option>
                                  <option value="focused">Focused</option>
                                </select>
                              </label>
                              <label
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 4,
                                  fontSize: 12,
                                  color: "var(--text-2)",
                                }}
                              >
                                Comparisons
                                <select
                                  value={u.comparisonMode ?? "aggressive"}
                                  onChange={(e) =>
                                    changeHintPrefs(u.uid, {
                                      comparisonMode: e.target.value as
                                        | "aggressive"
                                        | "conservative",
                                    })
                                  }
                                  style={{
                                    padding: "6px 10px",
                                    borderRadius: 8,
                                    border: "1px solid var(--border)",
                                    fontSize: 13,
                                  }}
                                >
                                  <option value="aggressive">Frequent</option>
                                  <option value="conservative">Only when relevant</option>
                                </select>
                              </label>
                            </div>
                            <UserMeetings uid={u.uid} />
                          </>
                        )}
                      </div>
                    );
                  })}
                </>
              )}
            </section>
          </>
        )}

        {adminTab === "knowledge" && <KnowledgeBaseSection />}
        {adminTab === "feedback" && <ReviewsSection />}
        {adminTab === "report" && <DailyReportSection />}
        {adminTab === "bugs" && <ClientErrorsSection />}
        {adminTab === "voiceoffield" && <VoiceOfFieldSection />}
        {adminTab === "marketplace" && (
          <>
            <ErrorBoundary label="admin-market-approvals">
              <MarketplaceApprovalsSection />
            </ErrorBoundary>
            <ErrorBoundary label="admin-plugin-approvals">
              <PluginApprovalsSection />
            </ErrorBoundary>
          </>
        )}
        {adminTab === "devrequests" && isDevAdmin && <DevRequestsSection />}
      </div>
    </div>
  );
}
